import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { MentionsInput, Mention } from "react-mentions";
import moment from "moment";
import secureLocalStorage from "react-secure-storage";
import ChatService from "../../../Services/ChatService";
import SocketService from "../../../Services/SocketService";
import DataParser from "../../../utlits/Dataparser";
import MessageInput from "./MessageInput";
import { useChatContext } from "./ChatContext";
import { Modal, Avatar, Tooltip, Empty, Spin, Input, Button, Dropdown, Menu, message, Popover, DatePicker } from "antd";
import { UserPlus, Info, MoreVertical, Trash2, LogOut, Eraser, Reply, SmilePlus, Search, ChevronLeft, X, Pin, Hash, MessageSquare, Users, LayoutGrid, ChevronDown, PlusCircle, AtSign, Smile, Mic, Pause, Play, Camera, Megaphone, MessagesSquare, Lightbulb, Bell, SendHorizontal, Paperclip, ThumbsUp, Edit2, Plus, Video, Image, FileText, Link2, Tag, Star, Zap, Heart, Flag, Bookmark, AlertCircle, CheckCircle, Check, CheckCheck, Clock, CornerUpRight } from "lucide-react";
import EmojiPicker from "emoji-picker-react";
import { FaThumbsUp, FaSmile, FaThumbtack, FaReply, FaShare, FaPen, FaTrash } from "react-icons/fa";
import "./Chat.css";

const mentionInputStyle = {
  control: {
    backgroundColor: 'transparent',
    fontSize: 14,
    fontFamily: 'inherit',
    lineHeight: '1.5',
    fontWeight: 'normal',
    width: '100%',
    flex: 1,
    minHeight: 24,
    maxHeight: 100,
  },
  '&multiLine': {
    control: {
      fontFamily: 'inherit',
      minHeight: 24,
    },
    highlighter: {
      padding: 0,
      border: 'none',
      lineHeight: '1.5',
      fontSize: 14,
      fontFamily: 'inherit',
      color: '#1d1d1d',
      boxSizing: 'border-box',
    },
    input: {
      padding: 0,
      border: 'none',
      outline: 'none',
      fontSize: 14,
      lineHeight: '1.5',
      fontFamily: 'inherit',
      color: '#1d1d1d',
      background: 'transparent',
      boxShadow: 'none',
      resize: 'none',
      minHeight: 24,
      maxHeight: 100,
      overflow: 'auto',
      boxSizing: 'border-box',
    },
  },
  suggestions: {
    zIndex: 999999,
    backgroundColor: '#fff',
    list: {
      backgroundColor: '#fff',
      border: '1px solid rgba(0,0,0,0.12)',
      fontSize: 14,
      borderRadius: '8px',
      overflow: 'hidden',
      boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
      zIndex: 999999,
      position: 'absolute',
      bottom: '100%',
      marginBottom: '24px',
      minWidth: 200,
    },
    item: {
      padding: '8px 12px',
      backgroundColor: '#fff',
      color: '#1d1d1d',
      borderBottom: '1px solid rgba(0,0,0,0.05)',
      cursor: 'pointer',
      '&focused': {
        backgroundColor: '#f5f5f5',
        color: '#1d1d1d',
      },
    },
  },
};

// Compress images before upload — reduces upload time dramatically for large photos
const compressImage = (file, maxWidth = 1920, quality = 0.85) =>
  new Promise((resolve) => {
    if (!file.type.startsWith('image/') || file.size < 300 * 1024) {
      resolve(file); // skip non-images and already-small files
      return;
    }
    const blobUrl = URL.createObjectURL(file);
    const img = new window.Image();
    img.onload = () => {
      URL.revokeObjectURL(blobUrl);
      let { width, height } = img;
      if (width > maxWidth) { height = Math.round((height * maxWidth) / width); width = maxWidth; }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => {
        if (!blob || blob.size >= file.size) { resolve(file); return; }
        resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg', lastModified: Date.now() }));
      }, 'image/jpeg', quality);
    };
    img.onerror = () => { URL.revokeObjectURL(blobUrl); resolve(file); };
    img.src = blobUrl;
  });

// Module-level set so preloaded URLs persist across re-renders
const _preloadedUrls = new Set();
const preloadImage = (url) => {
  if (!url || _preloadedUrls.has(url)) return;
  _preloadedUrls.add(url);
  const img = new window.Image();
  img.src = url;
};

const getImageUrl = (url) => {
  if (!url || url === "null") return null;
  if (url.startsWith('http')) return url;
  const baseUrl = (process.env.REACT_APP_BASE_URL || "http://localhost:5026/api/v1").split("/api/v1")[0];

  if (url.includes('/')) {
    const normalizedUrl = url.startsWith("/") ? url : `/${url}`;
    return `${baseUrl}${normalizedUrl.startsWith("/public") ? normalizedUrl : `/public${normalizedUrl}`}`;
  }

  // Plain filename — user profile images live under USER_DOCS_PATH (app-wide convention)
  return (process.env.REACT_APP_USER_DOCS_PATH || `${baseUrl}/public/users/docs/`) + url;
};

const shimmerStyle = {
  background: "linear-gradient(90deg,#f0f0f0 25%,#e8e8e8 50%,#f0f0f0 75%)",
  backgroundSize: "600px 100%",
  animation: "chat-shimmer 1.4s infinite linear",
  borderRadius: 6,
};

const ChatShimmer = () => {
  const rows = [
    { own: false, lines: [200, 140] },
    { own: true,  lines: [160] },
    { own: false, lines: [240, 120, 80] },
    { own: true,  lines: [180, 100] },
    { own: false, lines: [130] },
    { own: true,  lines: [220, 80] },
    { own: false, lines: [170, 110] },
  ];
  return (
    <div style={{ display: "flex", flexDirection: "column", padding: "16px 24px", gap: 16, flex: 1 }}>
      {rows.map((row, i) => (
        <div key={i} style={{ display: "flex", alignItems: "flex-end", gap: 10, flexDirection: row.own ? "row-reverse" : "row" }}>
          {/* avatar */}
          <div style={{ ...shimmerStyle, width: 28, height: 28, borderRadius: "50%", flexShrink: 0 }} />
          {/* bubble lines */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6, maxWidth: "60%" }}>
            {row.lines.map((w, j) => (
              <div key={j} style={{ ...shimmerStyle, height: 14, width: w }} />
            ))}
            <div style={{ ...shimmerStyle, height: 8, width: 40, alignSelf: row.own ? "flex-end" : "flex-start" }} />
          </div>
        </div>
      ))}
    </div>
  );
};

const ChatModal = () => {
  const { rooms, setRooms, activeRoom, setActiveRoom, isModalOpen, closeChat, openChat, anchorRect, setAnchorRect, hideConversation, isNewDmMode, isNewChannelMode, setIsInboxOpen, setIsChannelsOpen, refreshRooms } = useChatContext();

  const [messages, setMessages] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const [showPinnedPanel, setShowPinnedPanel] = useState(false);
  const [typingUsers, setTypingUsers] = useState({});
  const [isNewChatModal, setIsNewChatModal] = useState(false);
  const [isAddMemberModal, setIsAddMemberModal] = useState(false);
  const [isInfoModalVisible, setIsInfoModalVisible] = useState(false);
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [chatSearchQuery, setChatSearchQuery] = useState("");
  const [parentRoomId, setParentRoomId] = useState(null);
  const [modalType, setModalType] = useState("dm");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [newChannelName, setNewChannelName] = useState("");
  const [mentionUsers, setMentionUsers] = useState([]);
  const [urlPreviews, setUrlPreviews] = useState({});
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [dateRangeSelected, setDateRangeSelected] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState(null);
  const [floatingDateLabel, setFloatingDateLabel] = useState("Today's Chat");
  const [postTypePopoverOpen, setPostTypePopoverOpen] = useState(false);
  const [activePostTypeFilter, setActivePostTypeFilter] = useState(null);

  const [shouldRender, setShouldRender] = useState(isModalOpen);
  const [isClosing, setIsClosing] = useState(false);
  const [roomToDelete, setRoomToDelete] = useState(null);

  useEffect(() => {
    if (isModalOpen) {
      setShouldRender(true);
      setIsClosing(false);
      const scrollY = window.scrollY;
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";
    } else if (shouldRender) {
      // Close all overlays/modals when the chat popup closes
      setShowShareModal(false);
      setOpenPermDropdown(null);
      setChatMemberEditId(null);
      setChatMemberPopoverOpen(false);
      setShowAttachmentsPanel(false);
      setShowPinnedPanel(false);
      setShowDmEmojiPicker(false);
      setOpenReactionPopoverId(null);
      setIsClosing(true);
      const scrollY = Math.abs(parseInt(document.body.style.top || "0", 10));
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      window.scrollTo(0, scrollY);
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 800); // Match CSS animation duration
      return () => clearTimeout(timer);
    }
  }, [isModalOpen]);

  const channelMentionUsers = React.useMemo(() => {
    if (!activeRoom || activeRoom.type !== "channel") return mentionUsers;
    const memberIds = new Set((activeRoom.members || []).map(m => String(m.id || m._id)));
    return mentionUsers.filter(u => memberIds.has(String(u.id)));
  }, [activeRoom, mentionUsers]);
  const [chatMemberPopoverOpen, setChatMemberPopoverOpen] = useState(false);
  const [showAttachmentsPanel, setShowAttachmentsPanel] = useState(false);
  const [attachmentsTab, setAttachmentsTab] = useState("Images");
  const [msgSearchOpen, setMsgSearchOpen] = useState(false);
  const [msgSearchQuery, setMsgSearchQuery] = useState("");
  const [msgSearchFocused, setMsgSearchFocused] = useState(false);
  const msgSearchRef = React.useRef(null);
  const [showAddPeoplePanel, setShowAddPeoplePanel] = useState(false);
  const [showCreateChannelPanel, setShowCreateChannelPanel] = useState(false);
  const [channelNameError, setChannelNameError] = useState("");
  const [isCreatingChannel, setIsCreatingChannel] = useState(false);
  const [addPeopleSearch, setAddPeopleSearch] = useState("");
  const [addPeopleResults, setAddPeopleResults] = useState([]);
  const [selectedForInvite, setSelectedForInvite] = useState([]);
  const [selectedUsersMap, setSelectedUsersMap] = useState({});
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareEmail, setShareEmail] = useState("");
  const [memberPermissions, setMemberPermissions] = useState({});
  const [openPermDropdown, setOpenPermDropdown] = useState(null);
  const [permDropdownPos, setPermDropdownPos] = useState({ top: 0, right: 0 });
  const [chatMemberEditId, setChatMemberEditId] = useState(null);
  const [chatMemberEditPos, setChatMemberEditPos] = useState({ top: 0, left: 0 });
  const [inviteAccess, setInviteAccess] = useState("Comment");
  const [myPermission, setMyPermission] = useState("Full edit");
  const [inviteLink, setInviteLink] = useState("");
  const [isInviting, setIsInviting] = useState(false);

  // Voice Note Recording State
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  const shouldSendVoiceNote = useRef(false);

  const [sidebarWidth, setSidebarWidth] = useState(70);
  const [beakTop, setBeakTop] = useState(null);
  const [selectedPostType, setSelectedPostType] = useState(null);
  const [dmText, setDmText] = useState("");
  const [dmAttachments, setDmAttachments] = useState([]);
  const [showDmEmojiPicker, setShowDmEmojiPicker] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [editingAttachments, setEditingAttachments] = useState([]);
  const [replyTo, setReplyTo] = useState(null);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [deleteFor, setDeleteFor] = useState("everyone"); // "self" | "everyone"
  const [forwardMsg, setForwardMsg] = useState(null);
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [forwardSearch, setForwardSearch] = useState("");
  const [forwardSendingIds, setForwardSendingIds] = useState(new Set());
  const [forwardSentIds, setForwardSentIds] = useState(new Set());
  const [openReactionPopoverId, setOpenReactionPopoverId] = useState(null);
  const [reactionPickerDir, setReactionPickerDir] = useState({ v: 'up', h: 'right' });

  useEffect(() => {
    if (!openReactionPopoverId) return;
    const close = (e) => { if (!e.target.closest('.reaction-picker-wrapper')) setOpenReactionPopoverId(null); };
    const t = setTimeout(() => document.addEventListener('mousedown', close), 0);
    return () => { clearTimeout(t); document.removeEventListener('mousedown', close); };
  }, [openReactionPopoverId]);

  const dmFileInputRef = useRef(null);
  const dmTextareaRef = useRef(null);
  const chatMemberBtnRef = useRef(null);
  const notificationSound = useRef(new Audio("https://assets.mixkit.co/active_storage/sfx/2358/2358-preview.mp3"));
  // Stable ref so socket callbacks registered once always see the latest activeRoom
  const activeRoomRef = useRef(activeRoom);
  useEffect(() => { activeRoomRef.current = activeRoom; }, [activeRoom]);
  // Message send queue — processes one at a time so rapid sends never collide
  const sendQueueRef = useRef([]);
  const isProcessingQueueRef = useRef(false);
  const [customPostTypes, setCustomPostTypes] = useState([]);
  const [showCreatePostTypeModal, setShowCreatePostTypeModal] = useState(false);
  const [newPostTypeName, setNewPostTypeName] = useState("");
  const [newPostTypeColor, setNewPostTypeColor] = useState("#0DDFAB");
  const [newPostTypeIcon, setNewPostTypeIcon] = useState("Tag");

  const currentUser = DataParser.GetLoggedInUser();
  const currentUserId = currentUser?.user?.userdata?.id;

  // Returns true when the OTHER user in a DM is currently online
  const receiverOnline = (() => {
    if (!activeRoom || activeRoom.type !== 'direct') return false;
    const other = (activeRoom.members || []).find(m => String(m.id || m._id) !== String(currentUserId));
    return !!other?.isOnline;
  })();

  // is_read from the API is an array of user IDs who've read the message.
  // For own messages, it's "read" only if someone OTHER than the sender has read it.
  const getAttachmentPreview = (msg) => {
    if (!msg) return "";
    if (msg.text) return msg.text;
    const atts = msg.attachments || [];
    if (!atts.length) return "";
    const isAudio = (a) => a.fileType === "audio" || /\.(mp3|wav|webm|m4a|ogg)$/i.test(a.url || "");
    const isImage = (a) => a.fileType === "image" || /\.(jpg|jpeg|png|gif|webp)$/i.test(a.url || "");
    const images = atts.filter(a => isImage(a) && !isAudio(a));
    const audios = atts.filter(isAudio);
    const others = atts.filter(a => !isImage(a) && !isAudio(a));
    const parts = [];
    if (images.length === 1) parts.push(images[0].originalName || images[0].name || "Image");
    else if (images.length > 1) parts.push(`${images.length} Images`);
    if (audios.length > 0) parts.push("Voice Note");
    if (others.length === 1) parts.push(others[0].originalName || others[0].name || "File");
    else if (others.length > 1) parts.push(`${others.length} Files`);
    return parts.join(", ") || "Attachment";
  };

  const isMsgRead = (msg) => {
    const r = msg.is_read;
    if (!r || r === false) return false;
    if (Array.isArray(r)) {
      if (r.length === 0) return false;
      const senderId = String(msg.sender_id);
      return r.some(id => String(id) !== senderId);
    }
    return !!r; // boolean fallback for optimistic messages set to `false`
  };

  // DMs: anyone can hide; channels & project rooms: only the creator can
  const canDeleteConversation = activeRoom && (
    activeRoom.type === 'direct' ||
    String(activeRoom.created_by) === String(currentUserId)
  );

  const scrollRef = useRef(null);

  const renderMessageText = (text) => {
    if (!text) return "";
    
    // 1. Detect standard react-mentions markup: @[Name](id)
    const mentionMarkupRegex = /(@\[[^\]]+\]\(\d+\))/g;
    const parts = text.split(mentionMarkupRegex);
    
    return parts.map((part, i) => {
      const markupMatch = part.match(/@\[([^\]]+)\]\((\d+)\)/);
      if (markupMatch) {
        return <span key={`m-${i}`} style={{ backgroundColor: "#0DDFAB", color: "#1d1d1d", fontWeight: "600", borderRadius: "4px", padding: "1px 6px", display: "inline-block", lineHeight: "1.4", fontSize: "0.9em" }}>@{markupMatch[1]}</span>;
      }
      
      // 2. For the rest, detect plain text mentions: @FirstName LastName
      const members = activeRoom?.members || [];
      if (members.length > 0) {
        // Create map of names to highlight
        const memberNames = members.map(m => {
          const first = m.first_name || "";
          const last = m.last_name || "";
          return `${first} ${last}`.trim();
        }).filter(n => n.length > 0);
        
        memberNames.sort((a, b) => b.length - a.length); // Longest first
        
        if (memberNames.length > 0) {
          const namesRegex = new RegExp(`@(${memberNames.map(n => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'g');
          const subParts = part.split(namesRegex);
          
          let reassembled = [];
          for (let j = 0; j < subParts.length; j++) {
            if (memberNames.includes(subParts[j])) {
              reassembled.push(<span key={`pm-${i}-${j}`} style={{ backgroundColor: "#0DDFAB", color: "#1d1d1d", fontWeight: "600", borderRadius: "4px", padding: "1px 6px", display: "inline-block", lineHeight: "1.4", fontSize: "0.9em" }}>@{subParts[j]}</span>);
            } else {
              // Further split by URLs
              const urlRegex = /(https?:\/\/\S+|www\.\S+)/g;
              const urlParts = subParts[j].split(urlRegex);
              urlParts.forEach((up, k) => {
                if (/^(https?:\/\/|www\.)\S+/.test(up)) {
                  const href = up.startsWith('http') ? up : `https://${up}`;
                  reassembled.push(<a key={`u-${i}-${j}-${k}`} href={href} target="_blank" rel="noopener noreferrer" style={{ color: "#1a73e8", textDecoration: "underline" }}>{up}</a>);
                } else {
                  reassembled.push(up);
                }
              });
            }
          }
          return reassembled;
        }
      }

      // 3. Just detect URLs if no member names match
      const urlRegex = /(https?:\/\/\S+|www\.\S+)/g;
      const urlParts = part.split(urlRegex);
      return urlParts.map((up, j) => {
        if (/^(https?:\/\/|www\.)\S+/.test(up)) {
          const href = up.startsWith('http') ? up : `https://${up}`;
          return <a key={`u-${i}-${j}`} href={href} target="_blank" rel="noopener noreferrer" style={{ color: "#1a73e8", textDecoration: "underline" }}>{up}</a>;
        }
        return up;
      });
    });
  };

  const isDeletedMember = (m) => !m || m.is_deleted === true || m.status === 0 || m.status === "0";

  const getRoomDisplayName = (room) => {
    if (!room) return "";
    if (room.type === "direct" && room.members?.length > 0) {
      const other = room.members.find(m => String(m.id || m._id) !== String(currentUserId));
      if (isDeletedMember(other)) return "Deleted User";
      return `${other.first_name || ""} ${other.last_name || ""}`.trim() || "Deleted User";
    }
    return room.name || "Unnamed Room";
  };

  const getRoomDisplayAvatar = (room) => {
    if (room?.type === "direct") {
      const other = room.members.find(m => String(m.id || m._id) !== String(currentUserId));
      if (isDeletedMember(other)) return null;
      return other?.image ? getImageUrl(other.image) : null;
    }
    return null;
  };

  const handleScroll = (e) => {
    const container = e.target;
    const dividers = Array.from(container.querySelectorAll(".chat-date-divider"));
    if (dividers.length === 0) return;

    const containerTop = container.getBoundingClientRect().top;
    // Default to the first (oldest) divider so the top of the list always shows a date
    let currentLabel = dividers[0].getAttribute("data-date-label");

    for (const div of dividers) {
      const divTop = div.getBoundingClientRect().top - containerTop;
      if (divTop <= 80) {
        currentLabel = div.getAttribute("data-date-label");
      }
    }
    setFloatingDateLabel(currentLabel);
  };

  // Reload messages when active room changes
  useEffect(() => {
    if (activeRoom) {
      setMessages([]);
      setReplyTo(null);
      setMsgLoading(true);
      setDatePickerOpen(false);
      setDateRangeSelected(false);
      setPostTypePopoverOpen(false);
      setActivePostTypeFilter(null);
      setChatMemberPopoverOpen(false);
      setShowAttachmentsPanel(false);
      setShowPinnedPanel(false);
      setShowAddPeoplePanel(false);
      setIsSearchVisible(false);
      setChatSearchQuery("");
      setMsgSearchOpen(false);
      setMsgSearchQuery("");
      setMsgSearchFocused(false);
      setFloatingDateLabel(activeRoom.type === 'channel' ? "Today's Discussion" : "Today's Chat");
      setCustomPostTypes([]);
      setSelectedPostType(null);
      setMyPermission("Full edit");
      setMemberPermissions({});
      setInviteLink("");
      setShowShareModal(false);
      setOpenPermDropdown(null);
      setChatMemberEditId(null);
      setShowDmEmojiPicker(false);
      setDmText("");
      setDmAttachments([]);
      setEditingMessage(null);
      setEditingAttachments([]);
      setOpenReactionPopoverId(null);
      // Preload all room member avatars so they're in browser cache before messages render
      (activeRoom.members || []).forEach(m => { if (m.image) preloadImage(getImageUrl(m.image)); });
      SocketService.emit("joinChatRoom", activeRoom._id);
      fetchMessages();
      ChatService.markAsRead(activeRoom._id).catch(() => {});
      SocketService.emit("messagesRead", { room_id: activeRoom._id, user_id: String(currentUserId) });
      // Clear force_unread locally when room is opened
      setRooms(prev => prev.map(r => r._id === activeRoom._id ? { ...r, unreadCount: 0, force_unread: false } : r));
      fetchPinnedMessages();
      if (activeRoom.type === 'channel') {
        ChatService.getPostTypes(activeRoom._id)
          .then(res => { if (res.Good) setCustomPostTypes(res.customPostTypes); })
          .catch(() => {});
        ChatService.getRoomPermissions(activeRoom._id)
          .then(res => {
            if (res.Good) {
              const map = {};
              (res.memberPermissions || []).forEach(p => { map[String(p.userId)] = p.permission; });
              setMemberPermissions(map);
              setMyPermission(map[String(currentUserId)] || "Full edit");
            }
          })
          .catch(() => {});
      }
    }
  }, [activeRoom?._id]);

  // Preload current user's own avatar once on mount so own messages never flash initials
  useEffect(() => {
    const ownImage = currentUser?.user?.userdata?.image;
    if (ownImage) preloadImage(getImageUrl(ownImage));
  }, []);

  // Fetch mention users on mount
  useEffect(() => {
    if (!currentUser?.user?.userdata?.company_id) return;
    ChatService.getMentionUsers(currentUser.user.userdata.company_id)
      .then(res => {
        if (res.Good) setMentionUsers(res.data.user.map(u => ({ id: u.id, display: u.full_name })));
      })
      .catch(() => {});
  }, []);


  // Socket listeners for message-level events — registered ONCE, use refs for current values
  // connect() + registerUser are handled in ChatContext so the socket is ready before the modal opens
  useEffect(() => {
    const handleNewMessage = (data) => {
      if (data.sender_image) preloadImage(getImageUrl(data.sender_image));
      const room = activeRoomRef.current;
      if (room && String(room._id) === String(data.room_id)) {
        if (String(data.sender_id) !== String(currentUserId)) {
          // Receiver is actively viewing this room — mark as read immediately so sender gets blue ticks
          ChatService.markAsRead(room._id).catch(() => {});
          SocketService.emit("messagesRead", { room_id: room._id, user_id: String(currentUserId) });
        }
        setMessages(prev => {
          if (String(data.sender_id) === String(currentUserId)) return prev;
          if (prev.find(m => (m._id || m.id) === (data._id || data.id))) return prev;
          return [...prev, data];
        });
        scrollToBottom();
      }
      // Sound is handled by ChatContext (always mounted) — do not play here to avoid double sound
    };

    const handleTyping = (data) => {
      const { room_id, user_id, user_name, isTyping } = data;
      setTypingUsers(prev => {
        const roomTyping = { ...(prev[room_id] || {}) };
        if (isTyping) roomTyping[user_id] = user_name;
        else delete roomTyping[user_id];
        return { ...prev, [room_id]: roomTyping };
      });
    };

    const handleReaction = (data) => {
      setMessages(prev => prev.map(m => m._id === data.messageId ? { ...m, reactions: data.reactions } : m));
    };

    const handlePinned = (data) => {
      setMessages(prev => prev.map(m => m._id === data.messageId ? { ...m, is_pinned: data.isPinned } : m));
      if (data.isPinned) {
        setPinnedMessages(prev => prev.find(p => p._id === data.messageId) ? prev : [...prev, data.message]);
      } else {
        setPinnedMessages(prev => prev.filter(m => m._id !== data.messageId));
      }
    };

    const handleOnline = (uid) => {
      setRooms(prev => prev.map(r => ({ ...r, members: (r.members || []).map(m => String(m.id || m._id) === String(uid) ? { ...m, isOnline: true } : m) })));
      if (activeRoomRef.current) setActiveRoom(prev => prev ? ({ ...prev, members: (prev.members || []).map(m => String(m.id || m._id) === String(uid) ? { ...m, isOnline: true } : m) }) : prev);
    };

    const handleOffline = (uid) => {
      setRooms(prev => prev.map(r => ({ ...r, members: (r.members || []).map(m => String(m.id || m._id) === String(uid) ? { ...m, isOnline: false } : m) })));
      if (activeRoomRef.current) setActiveRoom(prev => prev ? ({ ...prev, members: (prev.members || []).map(m => String(m.id || m._id) === String(uid) ? { ...m, isOnline: false } : m) }) : prev);
    };

    const handleUpdate = (data) => {
      setMessages(prev => prev.map(m => m._id === data.messageId ? { ...m, text: data.text, is_edited: true } : m));
    };

    const handleDelete = (data) => {
      setMessages(prev => prev.filter(m => m._id !== data.messageId));
      setPinnedMessages(prev => prev.filter(m => m._id !== data.messageId));
    };

    const handleMemberJoined = ({ room }) => {
      setRooms(prev => prev.map(r => r._id === room._id ? { ...r, members: room.members } : r));
      if (activeRoomRef.current?._id === room._id) {
        setActiveRoom(prev => prev ? ({ ...prev, members: room.members }) : prev);
      }
    };

    const handleMessageDelivered = (data) => {
      setMessages(prev => prev.map(m =>
        (m._id === data.messageId || m.id === data.messageId)
          ? { ...m, isDelivered: true }
          : m
      ));
    };

    const handleMessagesRead = (data) => {
      if (String(data.user_id) === String(currentUserId)) return;
      if (String(activeRoomRef.current?._id) === String(data.room_id)) {
        setMessages(prev => prev.map(m =>
          String(m.sender_id) === String(currentUserId)
            ? { ...m, is_read: [data.user_id], isDelivered: true }
            : m
        ));
      }
    };

    const handleChatCleared = (data) => {
      if (String(activeRoomRef.current?._id) === String(data.roomId)) {
        setMessages([]);
      }
    };

    const handleUserLeftRoom = (data) => {
      const roomId = String(data.roomId);
      const leftUserId = String(data.userId);
      setRooms(prev => prev.map(r =>
        String(r._id) === roomId
          ? { ...r, members: (r.members || []).filter(m => String(m.id || m._id) !== leftUserId) }
          : r
      ));
      if (String(activeRoomRef.current?._id) === roomId) {
        setActiveRoom(prev => prev
          ? { ...prev, members: (prev.members || []).filter(m => String(m.id || m._id) !== leftUserId) }
          : prev
        );
      }
    };

    const handleRoomDeleted = (data) => {
      const roomId = String(data.roomId);
      setRooms(prev => prev.filter(r => String(r._id) !== roomId));
      if (String(activeRoomRef.current?._id) === roomId) {
        setActiveRoom(null);
      }
    };

    const handlePermissionUpdated = (data) => {
      if (String(data.room_id) !== String(activeRoomRef.current?._id)) return;
      setMemberPermissions(prev => {
        const map = { ...prev };
        (data.memberPermissions || []).forEach(p => { map[String(p.userId)] = p.permission; });
        return map;
      });
      if (String(data.userId) === String(currentUserId)) {
        setMyPermission(data.permission);
      }
    };

    SocketService.on("receiveChatMessage", handleNewMessage);
    SocketService.on("userTyping", handleTyping);
    SocketService.on("messageReaction", handleReaction);
    SocketService.on("messagePinned", handlePinned);
    SocketService.on("messageUpdated", handleUpdate);
    SocketService.on("messageDeleted", handleDelete);
    SocketService.on("userOnline", handleOnline);
    SocketService.on("userOffline", handleOffline);
    SocketService.on("memberJoined", handleMemberJoined);
    SocketService.on("messageDelivered", handleMessageDelivered);
    SocketService.on("messagesRead", handleMessagesRead);
    SocketService.on("chatCleared", handleChatCleared);
    SocketService.on("userLeftRoom", handleUserLeftRoom);
    SocketService.on("roomDeleted", handleRoomDeleted);
    SocketService.on("roomPermissionUpdated", handlePermissionUpdated);

    return () => {
      SocketService.off("receiveChatMessage", handleNewMessage);
      SocketService.off("userTyping", handleTyping);
      SocketService.off("messageReaction", handleReaction);
      SocketService.off("messagePinned", handlePinned);
      SocketService.off("messageUpdated", handleUpdate);
      SocketService.off("messageDeleted", handleDelete);
      SocketService.off("userOnline", handleOnline);
      SocketService.off("userOffline", handleOffline);
      SocketService.off("memberJoined", handleMemberJoined);
      SocketService.off("messageDelivered", handleMessageDelivered);
      SocketService.off("messagesRead", handleMessagesRead);
      SocketService.off("chatCleared", handleChatCleared);
      SocketService.off("userLeftRoom", handleUserLeftRoom);
      SocketService.off("roomDeleted", handleRoomDeleted);
      SocketService.off("roomPermissionUpdated", handlePermissionUpdated);
    };
  }, []);

  // Clear local state when switching to new-DM / new-channel mode (activeRoom = null)
  useEffect(() => {
    if (!activeRoom) {
      setMessages([]);
      setDmText("");
      setDmAttachments([]);
      setReplyTo(null);
      setEditingMessage(null);
      setEditingAttachments([]);
      setShowDmEmojiPicker(false);
      setShowAddPeoplePanel(false);
      setShowCreateChannelPanel(false);
      setNewChannelName("");
      setChannelNameError("");
      setShowAttachmentsPanel(false);
      setChatMemberPopoverOpen(false);
      setShowPinnedPanel(false);
      setPostTypePopoverOpen(false);
      setDatePickerOpen(false);
      setDateRangeSelected(false);
      setOpenReactionPopoverId(null);
      setSelectedPostType(null);
      setIsSearchVisible(false);
      setChatSearchQuery("");
      setShowShareModal(false);
      setOpenPermDropdown(null);
      setChatMemberEditId(null);
    }
  }, [activeRoom]);

  // Prevent the "Private workspace / empty state" flash when switching rooms.
  // useLayoutEffect fires BEFORE the browser paints — by setting msgLoading=true
  // and clearing messages here, the browser never paints the intermediate empty state.
  useLayoutEffect(() => {
    if (activeRoom?._id) {
      setMsgLoading(true);
      setMessages([]);
    }
  }, [activeRoom?._id]);

  const prevMsgsLen = useRef(0);
  // useLayoutEffect fires BEFORE the browser paints — the scroll happens in the same
  // frame as the render, so the user never sees messages sitting at the top.
  useLayoutEffect(() => {
    if (messages.length > prevMsgsLen.current) {
      scrollToBottom();
    }
    prevMsgsLen.current = messages.length;
  }, [messages]);

  // Dynamically track sidebar width and beak position
  useEffect(() => {
    if (!isModalOpen) return;

    const updateLayout = () => {
      // 1. Update sidebar width
      const mainContent = document.querySelector(".main-content");
      if (mainContent) {
        const rect = mainContent.getBoundingClientRect();
        // Minimal offset to maximize width
        const offset = rect.left < 100 ? 20 : 10;
        setSidebarWidth(rect.left + offset);
      }

      // 2. Determine beak target element
      const chatSubMenu = document.querySelector('#main-sidebar-chat-link + .sub-menu');
      const isChatFlyoutOpen = chatSubMenu && chatSubMenu.classList.contains('mm-show');
      const isSidebarExpanded = !document.body.classList.contains('vertical-collpsed');
      const isPinned = isChatFlyoutOpen && isSidebarExpanded;

      let targetEl = null;

      if (isPinned && activeRoom) {
        // Try to find the active room element in the flyout
        targetEl = document.querySelector(`[data-room-id="${activeRoom._id}"]`) ||
                   document.querySelector('.chat-item-link.active');
      }

      const modalEl = document.querySelector('.chat-modal-opening, .chat-modal-closing');
      const modalTop = modalEl ? modalEl.getBoundingClientRect().top : 8;

      // When no active room (new channel / new DM mode) but we have the anchor rect
      // from the + button that was clicked — use it directly for the beak position
      if ((!targetEl || targetEl.offsetParent === null) && !activeRoom && anchorRect) {
        const calculatedTop = anchorRect.top - modalTop + (anchorRect.height / 2) - 8;
        setBeakTop(prev => prev === calculatedTop ? prev : calculatedTop);
        return;
      }

      // If not pinned, or active room element not found/not visible, point to the main sidebar icon
      if (!targetEl || targetEl.offsetParent === null) {
        targetEl = document.getElementById('main-sidebar-chat-link');
      }

      if (targetEl) {
        const targetRect = targetEl.getBoundingClientRect();
        const calculatedTop = targetRect.top - modalTop + (targetRect.height / 2) - 8;
        setBeakTop(prev => prev === calculatedTop ? prev : calculatedTop);
      } else {
        setBeakTop(null);
      }
    };

    updateLayout();

    // Use ResizeObserver 
    const sidebar = document.querySelector(".vertical-menu");
    const observer = new ResizeObserver(updateLayout);
    if (sidebar) observer.observe(sidebar);
    
    // Watch body classes
    const bodyObserver = new MutationObserver(updateLayout);
    bodyObserver.observe(document.body, { attributes: true, attributeFilter: ["class"] });

    // Fallback polling for transition states
    const interval = setInterval(updateLayout, 100);

    window.addEventListener("resize", updateLayout);

    return () => {
      observer.disconnect();
      bodyObserver.disconnect();
      clearInterval(interval);
      window.removeEventListener("resize", updateLayout);
    };
  }, [isModalOpen, activeRoom, anchorRect]);

  const fetchMessages = async () => {
    try {
      const res = await ChatService.getMessages(activeRoom._id, 1, activeRoom.my_visible_from || null);
      if (res.Good) {
        // Preload all unique sender avatars from history so they never flash initials
        const seen = new Set();
        (res.messages || []).forEach(m => {
          if (m.sender_image && !seen.has(m.sender_image)) {
            seen.add(m.sender_image);
            preloadImage(getImageUrl(m.sender_image));
          }
        });
        setMessages(res.messages);
      }
    } catch (err) {}
    finally { setMsgLoading(false); }
  };

  const fetchPinnedMessages = async () => {
    try {
      const res = await ChatService.getPinnedMessages(activeRoom._id);
      if (res.Good) setPinnedMessages(res.pinned);
    } catch (err) {}
  };

  const handleTogglePin = async (messageId) => {
    try {
      const res = await ChatService.togglePin({ messageId });
      if (res.Good) {
        SocketService.emit("messagePinned", { room_id: activeRoom._id, messageId, isPinned: res.is_pinned, message: res.message });
        
        // Single pin rule: unpin all others locally
        setMessages(prev => prev.map(m => ({
          ...m,
          is_pinned: m._id === messageId ? res.is_pinned : false
        })));
        
        fetchPinnedMessages();
        message.success(res.is_pinned ? "Message pinned" : "Message unpinned");
      }
    } catch (err) { message.error("Failed to pin message"); }
  };

  // Voice Recording Functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const audioFile = new File([audioBlob], `voice-note-${Date.now()}.webm`, { type: 'audio/webm' });
        
        if (shouldSendVoiceNote.current) {
          handleSendMessage({ text: dmText, postType: selectedPostType, attachments: [audioFile] });
          setDmText("");
          setDmAttachments([]);
          setReplyTo(null);
          setEditingMessage(null);
          setShowDmEmojiPicker(false);
          shouldSendVoiceNote.current = false;
        } else {
          setDmAttachments(prev => [...prev, audioFile]);
        }
        
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setIsPaused(false);
      setRecordingTime(0);
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (err) {
      console.error("Error accessing microphone:", err);
      message.error("Microphone access denied");
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      clearInterval(recordingIntervalRef.current);
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "paused") {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && (isRecording || isPaused)) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      clearInterval(recordingIntervalRef.current);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Drains the send queue one message at a time — WhatsApp-style sequential delivery
  const processSendQueue = async () => {
    if (isProcessingQueueRef.current) return;
    isProcessingQueueRef.current = true;
    while (sendQueueRef.current.length > 0) {
      const job = sendQueueRef.current.shift();
      const { tempId, body, attachments, activeRoomId, replyTo, senderName, senderImage } = job;
      try {
        let messageData;
        if (attachments && attachments.length > 0) {
          // Compress all images in parallel before upload — big files go from MBs to KBs
          const readyFiles = await Promise.all(attachments.map(f => compressImage(f)));
          const formData = new FormData();
          Object.keys(body).forEach(k => { if (body[k] != null && body[k] !== "") formData.append(k, body[k]); });
          readyFiles.forEach(f => formData.append("attachments", f));
          const res = await ChatService.sendMessage(formData);
          messageData = res.message;
        } else {
          const res = await ChatService.sendMessage(body);
          messageData = res.message;
        }
        if (messageData) {
          const fullMsg = { ...messageData, sender_name: senderName, sender_image: senderImage, reply_to: replyTo };
          setMessages(prev => prev.map(m =>
            (m._id === tempId || m.id === tempId) ? { ...fullMsg, _stableKey: tempId, isSending: false, is_read: false } : m
          ));
          setRooms(prev => prev.map(r => r._id === activeRoomId ? { ...r, last_message: fullMsg, unreadCount: 0 } : r));
        }
      } catch (err) {
        console.error("Send message error", err);
        setMessages(prev => prev.map(m =>
          (m._id === tempId || m.id === tempId) ? { ...m, isSending: false, isFailed: true } : m
        ));
      }
    }
    isProcessingQueueRef.current = false;
  };

  const handleSendMessage = async (payload) => {
    if (!activeRoom) return;
    const { text, attachments, parent_id, postType } = payload;

    if (editingMessage) {
      try {
        const keepAttachmentIds = editingAttachments.map(a => a._id).filter(Boolean);
        let res;
        if (dmAttachments.length > 0) {
          const formData = new FormData();
          formData.append("messageId", editingMessage._id);
          formData.append("text", text || "");
          keepAttachmentIds.forEach(id => formData.append("keepAttachmentIds[]", id));
          dmAttachments.forEach(f => formData.append("attachments", f));
          res = await ChatService.editMessage(formData);
        } else {
          res = await ChatService.editMessage({ messageId: editingMessage._id, text, keepAttachmentIds });
        }
        const newFileAttachments = dmAttachments.map(f => ({
          name: f.name,
          originalName: f.name,
          fileType: f.type?.split('/')[0] || (f.name?.match(/\.(mp3|wav|webm|m4a|ogg)$/i) ? 'audio' : 'file'),
          url: URL.createObjectURL(f),
          isLocal: true,
        }));
        if (res.Good) {
          const updatedAttachments = res.message?.attachments
            ? res.message.attachments
            : [...editingAttachments, ...newFileAttachments];
          setMessages(prev => prev.map(m => m._id === editingMessage._id ? { ...m, text, attachments: updatedAttachments, is_edited: true } : m));
          SocketService.emit("messageUpdated", { messageId: editingMessage._id, text, room_id: activeRoom._id });
          setEditingMessage(null);
          setEditingAttachments([]);
          setDmAttachments([]);
          setDmText("");
        } else {
          console.error("Edit message failed:", res);
          message.error("Failed to edit message");
        }
      } catch (err) { console.error("Edit message error:", err); message.error("Failed to edit message"); }
      return;
    }

    const tempId = `temp-${Date.now()}`;
    const senderName = `${currentUser?.user?.userdata?.first_name || ""} ${currentUser?.user?.userdata?.last_name || ""}`.trim();
    const senderImage = currentUser?.user?.userdata?.image;

    // Show optimistic message immediately — isSending keeps the clock icon
    const tempMsg = {
      _id: tempId,
      id: tempId,
      _stableKey: tempId,
      text,
      sender_id: currentUserId,
      sender_name: senderName,
      sender_image: senderImage,
      createdAt: new Date().toISOString(),
      isSending: true,
      attachments: (attachments || []).map(f => ({
        name: f.name,
        fileType: f.type?.split('/')[0] || (f.name?.match(/\.(mp3|wav|webm|m4a|ogg)$/i) ? 'audio' : 'file'),
        url: URL.createObjectURL(f),
        isLocal: true
      })),
      reply_to: replyTo,
      postType: postType || null
    };

    setMessages(prev => [...prev, tempMsg]);
    setReplyTo(null);

    // Build processed text with @mention expansion
    let processedText = text;
    if (activeRoom?.members?.length > 0) {
      const sortedMembers = [...activeRoom.members].sort((a, b) => {
        const nA = `${a.first_name || ""} ${a.last_name || ""}`.trim();
        const nB = `${b.first_name || ""} ${b.last_name || ""}`.trim();
        return nB.length - nA.length;
      });
      sortedMembers.forEach(m => {
        const fullName = `${m.first_name || ""} ${m.last_name || ""}`.trim();
        if (fullName) {
          const escaped = fullName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          processedText = processedText.replace(new RegExp(`@${escaped}`, 'g'), `@[${fullName}](${m.id || m._id})`);
        }
      });
    }

    const body = { room_id: activeRoom._id, text: processedText, parent_id, reply_to: replyTo?._id || replyTo?.id || replyTo || null, postType: postType || null };

    // Push to queue — processSendQueue drains it one-at-a-time
    sendQueueRef.current.push({ tempId, body, attachments, activeRoomId: activeRoom._id, replyTo, senderName, senderImage });
    processSendQueue();
  };

  // Returns true if the message was sent within the last hour
  const isWithinOneHour = (msg) => {
    if (!msg?.createdAt) return false;
    return (Date.now() - new Date(msg.createdAt).getTime()) < 3600000;
  };

  const handleDeleteMessage = async (messageId, scope) => {
    try {
      const res = await ChatService.deleteMessage({ messageId, deleteFor: scope });
      if (res.Good) {
        // Always remove from local view
        setMessages(prev => prev.filter(m => m._id !== messageId));
        if (scope === "everyone") {
          // Notify all other users via socket
          SocketService.emit("messageDeleted", { messageId, room_id: activeRoom._id });
          message.success("Message deleted for everyone");
        } else {
          message.success("Message removed for you");
        }
      }
    } catch (err) { message.error("Failed to delete message"); }
  };

  const confirmDeleteMessage = async () => {
    if (!messageToDelete) return;
    await handleDeleteMessage(messageToDelete, deleteFor);
    setMessageToDelete(null);
    setDeleteFor("everyone");
    document.getElementById("closeChatMsgDeleteModal")?.click();
  };

  const closeForwardModal = () => {
    setShowForwardModal(false);
    setForwardMsg(null);
    setForwardSearch("");
    setForwardSendingIds(new Set());
    setForwardSentIds(new Set());
  };

  const handleForwardMessage = async (targetRoom) => {
    if (!forwardMsg || !targetRoom) return;
    if (forwardSendingIds.has(targetRoom._id) || forwardSentIds.has(targetRoom._id)) return;

    setForwardSendingIds(prev => new Set([...prev, targetRoom._id]));
    try {
      const baseUrl = (process.env.REACT_APP_BASE_URL || "http://localhost:5026/api/v1").split("/api/v1")[0];
      const bodyFields = {
        room_id: targetRoom._id,
        text: forwardMsg.text || "",
        is_forwarded: true,
        forwarded_from_name: forwardMsg.sender_name || "",
      };

      let res;
      if (forwardMsg.attachments?.length > 0) {
        const formData = new FormData();
        Object.entries(bodyFields).forEach(([k, v]) => { if (v !== null && v !== undefined && v !== "") formData.append(k, v); });
        await Promise.all(forwardMsg.attachments.map(async (att) => {
          try {
            const fileUrl = att.url?.startsWith("http") ? att.url : `${baseUrl}${att.url?.startsWith("/public") ? att.url : `/public${att.url}`}`;
            const fetchRes = await fetch(fileUrl);
            const blob = await fetchRes.blob();
            const file = new File([blob], att.originalName || att.name || "file", { type: blob.type || "application/octet-stream" });
            formData.append("attachments", file);
          } catch { /* skip unreadable */ }
        }));
        res = await ChatService.sendMessage(formData);
      } else {
        res = await ChatService.sendMessage(bodyFields);
      }

      if (res?.message && String(targetRoom._id) === String(activeRoomRef.current?._id)) {
        const fullMsg = {
          ...res.message,
          sender_name: `${currentUser?.user?.userdata?.first_name || ""} ${currentUser?.user?.userdata?.last_name || ""}`.trim(),
          sender_image: currentUser?.user?.userdata?.image,
          is_read: false,
          isSending: false,
        };
        setMessages(prev => {
          if (prev.find(m => (m._id || m.id) === (fullMsg._id || fullMsg.id))) return prev;
          return [...prev, fullMsg];
        });
        setRooms(prev => prev.map(r => r._id === targetRoom._id ? { ...r, last_message: fullMsg, unreadCount: 0 } : r));
      }
      // Mark this room as sent (keep modal open for more)
      setForwardSentIds(prev => new Set([...prev, targetRoom._id]));
    } catch (err) {
      message.error("Failed to forward message");
    } finally {
      setForwardSendingIds(prev => { const s = new Set(prev); s.delete(targetRoom._id); return s; });
    }
  };

  const handleDmPaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of Array.from(items)) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          const ext = item.type.split('/')[1]?.replace('jpeg', 'jpg') || 'png';
          const namedFile = new File([file], `pasted-image-${Date.now()}.${ext}`, { type: item.type });
          setDmAttachments(prev => [...prev, namedFile]);
        }
        break;
      }
    }
  };

  const handleTyping = (isTyping) => {
    if (!activeRoom) return;
    SocketService.emit("typing", {
      room_id: activeRoom._id,
      user_id: currentUserId,
      user_name: currentUser?.user?.userdata?.first_name || "Someone",
      isTyping,
    });
  };

  const handleCloseChat = () => {
    handleTyping(false);
    closeChat();
  };

  const handleSearchUsers = async (val) => {
    setSearchQuery(val);
    if (val.length < 2) return setSearchResults([]);
    try {
      const res = await ChatService.searchUsers(val);
      if (res.Good) setSearchResults(res.users);
    } catch (err) {}
  };

  const handleAddPeopleSearch = async (val) => {
    setAddPeopleSearch(val);
    if (val.length < 1) {
      try {
        const res = await ChatService.searchUsers("");
        if (res.Good) setAddPeopleResults(res.users);
      } catch (err) {}
      return;
    }
    try {
      const res = await ChatService.searchUsers(val);
      if (res.Good) setAddPeopleResults(res.users);
    } catch (err) {}
  };

  const toggleInviteUser = (user) => {
    const userId = user.id;
    setSelectedForInvite(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
    // Cache user object so chips still render after search text changes
    setSelectedUsersMap(prev => ({ ...prev, [userId]: user }));
  };

  const closeShareModal = () => {
    setShowShareModal(false);
    setOpenPermDropdown(null);
    setShareEmail("");
    setInviteAccess("Comment");
    setInviteLink("");
    setSelectedForInvite([]);
    setSelectedUsersMap({});
  };

  const handleSendInvite = async () => {
    if (!activeRoom || isInviting) return;
    const selected = selectedForInvite.map(id => selectedUsersMap[id] || addPeopleResults.find(u => u.id === id)).filter(Boolean);
    if (selected.length === 0 && !shareEmail.trim()) return;
    setIsInviting(true);
    try {
      if (selected.length > 0) {
        for (const user of selected) {
          await ChatService.sendInvite({
            roomId: activeRoom._id,
            email: user.email,
            userId: user.id,
            permission: inviteAccess,
          });
        }
      } else {
        await ChatService.sendInvite({
          roomId: activeRoom._id,
          email: shareEmail.trim(),
          permission: inviteAccess,
        });
      }
      message.success("Invitation sent!");
    } catch (err) {
      message.error("Failed to send invitation");
    } finally {
      setIsInviting(false);
    }
    closeShareModal();
    setShowAddPeoplePanel(false);
    setAddPeopleSearch("");
    setAddPeopleResults([]);
  };

  // Build a correct frontend invite URL from whatever the backend returns.
  // The backend may return a full URL (wrong origin), just a token, or a path.
  // We always reconstruct using the current frontend origin so the link works.
  const buildInviteLink = (backendValue) => {
    if (!backendValue) return "";
    let token = backendValue;
    try {
      const parsed = new URL(backendValue);
      const param = parsed.searchParams.get("chat_invite");
      if (param) token = param;
    } catch {}
    return `${window.location.origin}/?chat_invite=${encodeURIComponent(token)}`;
  };

  // Accept invite token — only runs once the user is authenticated
  const processInviteToken = (token) => {
    ChatService.acceptInvite(token)
      .then(res => {
        if (res.Good) {
          // Add room immediately for instant UI feedback
          setRooms(prev => {
            const exists = prev.find(r => r._id === res.room._id);
            return exists ? prev.map(r => r._id === res.room._id ? res.room : r) : [res.room, ...prev];
          });
          // Ensure the socket is joined for this room right away
          SocketService.emit("joinChatRoom", res.room._id);
          openChat(res.room);
          message.success(`You've joined "${res.room.name}"!`);
          // Re-fetch room list after acceptance so the sidebar is fully in sync
          // (the initial getRooms call may have run before the user was added server-side)
          setTimeout(() => refreshRooms(), 1500);
        } else {
          message.error(res.message || "Invalid or expired invitation");
        }
      })
      .catch(() => {});
  };

  const location = useLocation();

  const INVITE_KEY = "planit_pending_chat_invite";
  const _authRoutes = ['/', '/forgot-password', '/initial-reset-password'];
  const _isAuthRoute = (p) => _authRoutes.includes(p) || p.startsWith('/reset-password');

  // Helper — reads LIVE browser state (not stale React state) before accepting invite.
  // Called inside a setTimeout so route-guard redirects have had time to settle.
  const _processIfSafe = (token) => {
    const livePath = window.location.pathname;
    const liveId = DataParser.GetLoggedInUser()?.user?.userdata?.id;
    if (liveId && !_isAuthRoute(livePath)) {
      localStorage.removeItem(INVITE_KEY);
      processInviteToken(token);
    }
    // If not safe the token remains in localStorage — next navigation will retry.
  };

  const NOTIF_ROOM_KEY = "planit_pending_notif_room";

  // On every navigation (and on mount): park any fresh invite token from the URL then
  // process any pending invite or notification room from localStorage.
  // Checking the URL here (not just on mount) covers already-logged-in users who navigate
  // directly to an invite link — ChatModal stays mounted so the mount-only effect never re-runs.
  useEffect(() => {
    // Park fresh token from URL first
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get("chat_invite");
    if (urlToken) {
      window.history.replaceState({}, "", window.location.pathname);
      localStorage.setItem(INVITE_KEY, urlToken);
    }

    const pendingInvite = localStorage.getItem(INVITE_KEY);
    const pendingRoomId = localStorage.getItem(NOTIF_ROOM_KEY);
    if (!pendingInvite && !pendingRoomId) return;

    const t = setTimeout(() => {
      const livePath = window.location.pathname;
      const liveId = DataParser.GetLoggedInUser()?.user?.userdata?.id;
      if (!liveId || _isAuthRoute(livePath)) return; // not safe yet — keys stay for next nav

      // Process pending invite
      if (pendingInvite) _processIfSafe(pendingInvite);

      // Open pending notification room — only remove key after successfully opening
      if (pendingRoomId) {
        const room = rooms.find(r => String(r._id) === pendingRoomId);
        if (room) {
          localStorage.removeItem(NOTIF_ROOM_KEY);
          openChat(room);
        }
        // If room not found yet (rooms still loading), key stays — the rooms-watcher below will catch it
      }
    }, 1000);

    return () => clearTimeout(t);
  }, [location.pathname]);

  // Safety net: once rooms are loaded, retry opening any pending notification room
  useEffect(() => {
    if (!rooms.length) return;
    const pendingRoomId = localStorage.getItem(NOTIF_ROOM_KEY);
    if (!pendingRoomId) return;
    const livePath = window.location.pathname;
    const liveId = DataParser.GetLoggedInUser()?.user?.userdata?.id;
    if (!liveId || _isAuthRoute(livePath)) return;
    const room = rooms.find(r => String(r._id) === pendingRoomId);
    if (room) {
      localStorage.removeItem(NOTIF_ROOM_KEY);
      openChat(room);
    }
  }, [rooms]);

  const handleStartDirectChat = async (userId) => {
    try {
      const res = await ChatService.getOrCreateRoom({ type: "direct", targetId: userId });
      if (res.Good) {
        const room = res.room;
        setRooms(prev => prev.find(r => r._id === room._id) ? prev : [room, ...prev]);
        openChat(room);
        setIsNewChatModal(false);
        setIsInboxOpen(true);
        // Auto-position beak to the newly active sidebar room item
        setTimeout(() => {
          const el = document.querySelector(`[data-room-id="${room._id}"]`) ||
                     document.querySelector('.chat-item-link.active');
          if (el && setAnchorRect) setAnchorRect(el.getBoundingClientRect());
        }, 200);
      }
    } catch (err) {}
  };

  const handleCreateNewChannel = async () => {
    if (!newChannelName.trim()) { setChannelNameError("Channel name is required"); return; }
    if (isCreatingChannel) return;
    setIsCreatingChannel(true);
    try {
      const res = await ChatService.getOrCreateRoom({ type: "channel", name: newChannelName.trim() });
      if (res.Good) {
        const room = res.room;
        setRooms(prev => prev.find(r => r._id === room._id) ? prev : [room, ...prev]);
        openChat(room);
        setIsChannelsOpen(true);
        setShowCreateChannelPanel(false);
        setNewChannelName("");
        setChannelNameError("");
        setTimeout(() => {
          const el = document.querySelector('.chat-item-link.active');
          if (el && setAnchorRect) setAnchorRect(el.getBoundingClientRect());
        }, 200);
      }
    } catch (err) {}
    finally { setIsCreatingChannel(false); }
  };

  const handleNewChat = (type = "dm", parentId = null) => {
    setModalType(type);
    setParentRoomId(parentId);
    setSearchQuery("");
    setSearchResults([]);
    setNewChannelName("");
    setIsNewChatModal(true);
  };

  const handleCreateChannel = async () => {
    if (!newChannelName.trim()) return;
    try {
      const payload = { type: "channel", name: newChannelName };
      if (parentRoomId) payload.parent_id = parentRoomId;
      const res = await ChatService.getOrCreateRoom(payload);
      if (res.Good) {
        const room = res.room;
        setRooms(prev => prev.find(r => r._id === room._id) ? prev : [room, ...prev]);
        openChat(room);
        setIsNewChatModal(false);
        setNewChannelName("");
        setParentRoomId(null);
      }
    } catch (err) {}
  };

  const handleConfirmAddMember = async (userId) => {
    if (!activeRoom) return;
    try {
      const res = await ChatService.addMember({ roomId: activeRoom._id, userId });
      if (res.Good) {
        const updatedRoom = { ...activeRoom, members: [...activeRoom.members, res.user] };
        setActiveRoom(updatedRoom);
        setRooms(prev => prev.map(r => r._id === activeRoom._id ? updatedRoom : r));
        // Notify other room members in real-time
        SocketService.emit("memberJoined", { room: updatedRoom });
        setIsAddMemberModal(false);
        setSearchQuery("");
        setSearchResults([]);
        message.success("Member added!");
      }
    } catch (err) {}
  };

  const clearDateFilter = () => {
    setSelectedDateRange(null);
    setDateRangeSelected(false);
    setFloatingDateLabel(activeRoom?.type === 'channel' ? "Today's Discussion" : "Today's Chat");
  };

  const handleClearChat = async () => {
    try {
      const res = await ChatService.clearMessages({ roomId: activeRoom._id });
      if (res.Good) {
        setMessages([]);
        SocketService.emit("chatCleared", { roomId: activeRoom._id });
        message.success("Chat history cleared");
      }
    } catch (err) { message.error("Failed to clear chat"); }
  };

  const handleLeaveRoom = async () => {
    try {
      const res = await ChatService.leaveRoom({ roomId: activeRoom._id });
      if (res.Good) {
        SocketService.emit("userLeftRoom", { roomId: activeRoom._id, userId: String(currentUserId) });
        setRooms(prev => prev.filter(r => r._id !== activeRoom._id));
        setActiveRoom(null);
        message.success("Left room successfully");
      }
    } catch (err) { message.error("Failed to leave room"); }
  };

  const handleDeleteRoom = async () => {
    try {
      const res = await ChatService.deleteRoom({ roomId: activeRoom._id });
      if (res.Good) {
        SocketService.emit("roomDeleted", { roomId: activeRoom._id });
        setRooms(prev => prev.filter(r => r._id !== activeRoom._id));
        setActiveRoom(null);
        message.success("Room deleted");
      }
    } catch (err) { message.error("Failed to delete room"); }
  };

  const openHideConversationModal = () => {
    if (!activeRoom) return;
    setRoomToDelete(activeRoom);
    document.getElementById("openDeleteConvModal")?.click();
  };

  const confirmHideConversation = () => {
    if (!roomToDelete) return;
    hideConversation(roomToDelete._id);
    setRoomToDelete(null);
    document.getElementById("closeDeleteConvModal")?.click();
  };

  const getReactionTooltip = (users) => {
    if (!users?.length) return '';
    const names = users.map(uid => {
      if (String(uid) === String(currentUserId)) return 'You';
      const member = (activeRoom?.members || []).find(m => String(m.id || m._id) === String(uid));
      return member ? `${member.first_name || ''} ${member.last_name || ''}`.trim() : 'Unknown';
    });
    return names.join(', ');
  };

  const handleToggleReaction = async (messageId, emoji) => {
    if (!messageId || String(messageId).startsWith("temp-")) return;
    try {
      const res = await ChatService.toggleReaction({ messageId, emoji });
      if (res.Good) {
        SocketService.emit("messageReaction", { room_id: activeRoom._id, messageId, reactions: res.reactions });
        setMessages(prev => prev.map(m => m._id === messageId ? { ...m, reactions: res.reactions } : m));
      }
    } catch (err) {}
  };

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  };


  const isVoiceNote = (msg) =>
    msg.attachments?.length > 0 &&
    msg.attachments.every(a => a.fileType === "audio" || /\.(mp3|wav|webm|m4a|ogg)$/i.test(a.url || "")) &&
    !msg.text;

  const getAttachmentUrl = (att) => {
    const base = (process.env.REACT_APP_BASE_URL || "http://localhost:5026/api/v1").split("/api/v1")[0];
    if (!att.url) return "";
    if (att.url.startsWith("http")) return att.url;
    return `${base}${att.url.startsWith("/public") ? att.url : `/public${att.url}`}`;
  };

  const renderEditAttachmentPills = () => {
    const hasAny = editingAttachments?.length > 0 || dmAttachments.length > 0;
    if (!hasAny) return null;
    const pillStyle = { display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#1D1D1D', borderRadius: '4px', padding: '0 8px', height: '24px', fontSize: '11px', color: '#0DDFAB', maxWidth: '200px', fontWeight: 500 };
    const xStyle = { flexShrink: 0, cursor: 'pointer', fontSize: '13px', lineHeight: 1, marginLeft: '2px' };
    return (
      <div className="d-flex flex-wrap" style={{ gap: '8px', padding: '10px 20px', borderBottom: '1px solid #eee' }}>
        {(editingAttachments || []).map((att, i) => {
          const isAudio = att.fileType === "audio" || /\.(mp3|wav|webm|m4a|ogg)$/i.test(att.url || "");
          const label = isAudio ? "Voice Note" : (att.originalName || att.name || "File");
          const Icon = isAudio ? Mic : Paperclip;
          return (
            <div key={`ea-${i}`} title={label} style={pillStyle}>
              <Icon size={11} style={{ flexShrink: 0 }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{label}</span>
              <span onClick={() => setEditingAttachments(prev => prev.filter((_, idx) => idx !== i))} style={xStyle}>×</span>
            </div>
          );
        })}
        {dmAttachments.map((file, i) => (
          <div key={`new-${i}`} title={file.name} style={pillStyle}>
            <i className="uil-import" style={{ fontSize: '11px', flexShrink: 0 }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{file.name}</span>
            <span onClick={() => setDmAttachments(prev => prev.filter((_, idx) => idx !== i))} style={xStyle}>×</span>
          </div>
        ))}
      </div>
    );
  };

  const scrollToMessage = (msgId) => {
    setTimeout(() => {
      const id = String(msgId);
      const el = document.getElementById(`msg-${id}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("highlight-message");
        setTimeout(() => el.classList.remove("highlight-message"), 2000);
      }
    }, 50);
  };

  const formatDateDivider = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    if (date.toDateString() === today.toDateString()) return "Today";
    if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
    return date.toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" });
  };

  const renderMessage = (msg, idx) => {
    const isOwn = String(msg.sender_id) === String(currentUserId);
    const senderMember = activeRoom?.members?.find(m => String(m.id || m._id) === String(msg.sender_id));
    const isSenderDeleted = isDeletedMember(senderMember) && senderMember !== undefined;
    const displaySenderName = isSenderDeleted ? "Deleted User" : (msg.sender_name || "Deleted User");
    const displaySenderImage = isSenderDeleted ? null : msg.sender_image;
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const urls = msg.text?.match(urlRegex);
    const reactionsList = msg.reactions || [];

    return (
      <div key={msg._id || idx} id={`msg-${msg._id}`} className={`message-item ${isOwn ? "own" : ""}`}>
        <div className="message-avatar" style={{ marginTop: '4px', overflow: 'hidden', background: isSenderDeleted ? '#9ca3af' : undefined }}>
          {displaySenderImage ? (
            <img src={getImageUrl(displaySenderImage)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            displaySenderName?.charAt(0)
          )}
        </div>
        <div className="message-wrapper" style={{ display: "flex", flexDirection: "column", alignItems: isOwn ? "flex-end" : "flex-start", minWidth: 0 }}>
          {activeRoom?.type !== "direct" && (
            <div style={{ display: "flex", alignItems: "baseline", gap: "8px", marginBottom: "2px", padding: "0 4px" }}>
              <div style={{ fontSize: "13px", fontWeight: "700", color: "#202124" }}>{isOwn ? "You" : displaySenderName} | {moment(msg.createdAt).format("h:mm A")}</div>
            </div>
          )}
          <div className="message-content" style={{ maxWidth: "100%" }}>
            {msg.is_pinned && (
              <div style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "11px", color: "#1a73e8", marginBottom: "4px" }}>
                <Pin size={10} fill="#1a73e8" /><span>Pinned</span>
              </div>
            )}
            {msg.parent_id && (
              <div className="quoted-message" style={{ background: "rgba(0,0,0,0.05)", borderLeft: "3px solid #1a73e8", padding: "5px 10px", borderRadius: "4px", marginBottom: "8px", fontSize: "11px", opacity: 0.8 }}>
                <div style={{ fontWeight: "bold", color: "#1a73e8" }}>{msg.parent_id.sender_name || "Deleted User"}</div>
                <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{msg.parent_id.text || "Attachment"}</div>
              </div>
            )}
            {msg.is_forwarded && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#8e8e8e', marginBottom: 4 }}>
                <CornerUpRight size={11} /><span>Forwarded</span>
              </div>
            )}
            {msg.is_edited && !msg.is_forwarded && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#8e8e8e', marginBottom: 4, fontStyle: 'italic' }}>
                <Edit2 size={10} /><span>Edited</span>
              </div>
            )}
            <div className="message-text-wrapper">
              <div>{renderMessageText(msg.text)}</div>
            </div>


            {!(msg.isSending || msg.isFailed) && <div className={`message-actions-toolbar${openReactionPopoverId === msg._id ? ' reaction-open' : ''}`}>
              <Tooltip title={msg.is_pinned ? "Unpin" : "Pin"}>
                <Pin size={16} onClick={() => handleTogglePin(msg._id)} style={{ cursor: "pointer", color: msg.is_pinned ? "#1a73e8" : "#5f6368" }} />
              </Tooltip>
              <Tooltip title="Reply">
                <Reply size={16} onClick={() => setReplyTo(msg)} style={{ cursor: "pointer" }} />
              </Tooltip>
              <Tooltip title="Forward">
                <CornerUpRight size={16} onClick={() => { setForwardMsg(msg); setShowForwardModal(true); }} style={{ cursor: "pointer" }} />
              </Tooltip>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <SmilePlus size={16} style={{ cursor: "pointer" }} onClick={(e) => { const rect = e.currentTarget.getBoundingClientRect(); setReactionPickerDir({ v: window.innerHeight - rect.bottom < 390 ? 'up' : 'down', h: rect.left + rect.width / 2 < window.innerWidth / 2 ? 'left' : 'right' }); setOpenReactionPopoverId(openReactionPopoverId === msg._id ? null : msg._id); }} />
                {openReactionPopoverId === msg._id && (
                  <div className="reaction-picker-wrapper" style={{ position: 'absolute', ...(reactionPickerDir.v === 'up' ? { bottom: '30px' } : { top: '30px' }), ...(reactionPickerDir.h === 'right' ? { right: 0 } : { left: 0 }), zIndex: 100, background: '#fff', borderRadius: '12px', boxShadow: '0 8px 30px rgba(0,0,0,0.15)', padding: '12px' }} onClick={e => e.stopPropagation()}>
                    <div className="float-input-wrap float-input-icon" style={{ marginBottom: '4px' }}>
                      <i className="uil-search" style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', color: '#aaa', fontSize: '15px', pointerEvents: 'none', zIndex: 2 }} />
                      <input type="text" placeholder=" " className="form-control-custom-focus" style={{ padding: '0 38px 0 14px', borderRadius: '8px', height: '38px', border: '1.5px solid #d0d0d0', lineHeight: '38px', width: '100%' }} onChange={e => { const val = e.target.value; const root = e.currentTarget.closest('.reaction-picker-wrapper'); const inp = root?.querySelector('input:not(.form-control-custom-focus)'); if (inp) { const s = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set; s.call(inp, val); inp.dispatchEvent(new Event('input', { bubbles: true })); inp.dispatchEvent(new Event('change', { bubbles: true })); } }} />
                      <label style={{ fontSize: '13px', left: '14px' }}>Search emojis...</label>
                    </div>
                    <EmojiPicker
                      onEmojiClick={(emojiData) => { handleToggleReaction(msg._id, emojiData.emoji); setOpenReactionPopoverId(null); }}
                      skinTonesDisabled
                      previewConfig={{ showPreview: false }}
                      width={350}
                      height={300}
                    />
                  </div>
                )}
              </div>
            </div>}
            {msg.attachments?.length > 0 && (() => {
              const baseUrl = (process.env.REACT_APP_BASE_URL || "http://localhost:5026/api/v1").split("/api/v1")[0];
              const getUrl = (f) => `${baseUrl}${f.url.startsWith("/public") ? f.url : `/public${f.url}`}`;
              const imgFiles   = msg.attachments.filter(f => f.fileType === "image" || /\.(jpg|jpeg|png|gif|webp)$/i.test(f.url));
              const otherFiles = msg.attachments.filter(f => !(f.fileType === "image" || /\.(jpg|jpeg|png|gif|webp)$/i.test(f.url)));
              const cols = imgFiles.length === 1 ? 1 : imgFiles.length === 2 ? 2 : 3;
              return (
                <div style={{ marginTop: 5 }}>
                  {imgFiles.length > 0 && (
                    <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 3, maxWidth: cols === 1 ? 200 : cols === 2 ? 244 : 270, borderRadius: 8, overflow: "hidden" }}>
                      {imgFiles.map((f, i) => (
                        <img key={i} src={getUrl(f)} alt="attachment" style={{ width: "100%", aspectRatio: "1/1", objectFit: "cover", display: "block", cursor: "pointer" }} onClick={() => window.open(getUrl(f), "_blank")} />
                      ))}
                    </div>
                  )}
                  {otherFiles.map((f, i) => (
                    <a key={i} href={getUrl(f)} target="_blank" rel="noreferrer" style={{ display: "block", background: "#eee", padding: "5px 10px", borderRadius: "4px", fontSize: "12px", marginTop: 4 }}>{f.name || "Download File"}</a>
                  ))}
                </div>
              );
            })()}
            {reactionsList.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "5px", marginTop: "8px" }}>
                {reactionsList.map((r, i) => (
                  <Tooltip key={i} title={getReactionTooltip(r.users)} placement="top">
                    <div onClick={() => handleToggleReaction(msg._id, r.emoji)} style={{ background: r.users?.includes(currentUserId) ? "#e8f0fe" : "#f1f3f4", border: r.users?.includes(currentUserId) ? "1px solid #1a73e8" : "1px solid #ddd", padding: "2px 8px", borderRadius: "12px", fontSize: "11px", display: "flex", alignItems: "center", gap: "4px", cursor: "pointer" }}>
                      <span>{r.emoji}</span><span>{r.users?.length}</span>
                    </div>
                  </Tooltip>
                ))}
              </div>
            )}
            <div className="message-info-wrapper">
              <span className="message-time-inner">
                {moment(msg.createdAt).format("h:mm A")}
              </span>
            </div>
            {urls && urls.map((url, i) => {
              const preview = urlPreviews[url];
              if (!preview) return null;
              return (
                <a key={i} href={url} target="_blank" rel="noreferrer" className="url-preview-card">
                  {preview.images?.[0] && <img src={preview.images[0]} alt="preview" />}
                  <div className="url-info">
                    <div className="url-title">{preview.title}</div>
                    <div className="url-desc">{preview.description}</div>
                    <div className="url-site">{preview.siteName || new URL(url).hostname}</div>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderMessages = () => {
    let lastDate = null;
    const filtered = chatSearchQuery.trim()
      ? messages.filter(m => m.text?.toLowerCase().includes(chatSearchQuery.toLowerCase()))
      : messages;

    if (filtered.length === 0 && chatSearchQuery.trim()) {
      return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", color: "#70757a" }}>
          <Search size={48} style={{ opacity: 0.2, marginBottom: "15px" }} />
          <div>No results for "{chatSearchQuery}"</div>
        </div>
      );
    }

    return filtered.map((msg, idx) => {
      const msgDate = new Date(msg.createdAt).toDateString();
      let showDivider = false;
      if (msgDate !== lastDate) { lastDate = msgDate; showDivider = true; }
      return (
        <React.Fragment key={msg._id || idx}>
          {showDivider && <div className="date-divider"><span>{formatDateDivider(msg.createdAt)}</span></div>}
          {renderMessage(msg, idx)}
        </React.Fragment>
      );
    });
  };

  if (!shouldRender) return null;

  return (
    <>
      {/* Modal Overlay / Wrapper */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          paddingLeft: sidebarWidth,
          zIndex: 10000,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          pointerEvents: "auto",
        }}
        onClick={handleCloseChat}
      >
        <div
          className={isClosing ? "chat-modal-closing" : "chat-modal-opening"}
          style={{
            width: "calc(100% - 30px)",
            maxWidth: "1600px",
            height: "calc(var(--app-vh, 100vh) - 16px)",
            background: "rgb(255, 255, 255)",
            borderRadius: "14px",
            display: "flex",
            flexDirection: "column",
            border: "1px solid #e8e8e8",
            boxShadow: "0 8px 40px rgba(0,0,0,0.18), 0 2px 12px rgba(0,0,0,0.10)",
            pointerEvents: "all",
            position: "relative"
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* DYNAMIC BEAK/POINTER - MOVED OUTSIDE CONTENT FOR VISIBILITY */}
          {(beakTop !== null || anchorRect) && (
            <div 
              className="chat-modal-beak"
              style={{
                position: 'absolute',
                left: '-8px',
                top: `${beakTop !== null ? beakTop : (anchorRect.top - (window.innerHeight * 0.02) + (anchorRect.height / 2))}px`,
                width: '16px',
                height: '16px',
                background: '#fff',
                transform: 'rotate(45deg)',
                borderLeft: '1px solid #e8e8e8',
                borderBottom: '1px solid #e8e8e8',
                boxShadow: '-3px 3px 8px rgba(0,0,0,0.08)',
                zIndex: 10,
                transition: 'top 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                pointerEvents: 'none'
              }}
            />
          )}
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', overflow: 'hidden', borderRadius: '14px' }}>
            {/* --- Channel Specific Layout --- */}
            {activeRoom?.type === "channel" ? (
            <div className="channel-layout" style={{ overflow: "visible" }}>
              {/* Modern Channel Header */}
              <div className="channel-header-modern">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div className="channel-title-row">
                    <div className="channel-icon-box">
                      <img src="/assets/images/chatusers.svg" alt="channel" style={{ width: 18, height: 18 }} />
                    </div>
                    <div className="channel-title-text">
                      Channel / {getRoomDisplayName(activeRoom)}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                    <div onClick={handleCloseChat} style={{ border: '1px solid #f0f0f0', background: '#fff', width: '28px', height: '28px', minWidth: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', cursor: 'pointer', padding: 0 }}>
                      <ChevronLeft size={12} color="#8e8e8e" className="back-btn-icon" strokeWidth={2.5} />
                    </div>
                  </div>
                </div>

                {activeRoom?.type === 'channel' && (
                <div className="channel-filters-row">
                  <Popover
                    content={
                      <div className="post-type-filter-popover">
                        {customPostTypes.map(pt => {
                          const iconMap = { Megaphone: <Megaphone size={14} />, MessagesSquare: <MessagesSquare size={14} />, Lightbulb: <Lightbulb size={14} />, Bell: <Bell size={14} />, Tag: <Tag size={14} />, Star: <Star size={14} />, Zap: <Zap size={14} />, Heart: <Heart size={14} />, Flag: <Flag size={14} />, Bookmark: <Bookmark size={14} />, AlertCircle: <AlertCircle size={14} />, CheckCircle: <CheckCircle size={14} /> };
                          const classMap = { Announcement: "announcement", Discussion: "discussion", Idea: "idea", Update: "update" };
                          const typeClass = classMap[pt.name] || "";
                          const count = messages.filter(m => (m.postType || m.type) === pt.name).length;
                          const isActive = activePostTypeFilter === pt.name;
                          return (
                            <div
                              key={pt.name}
                              className={`pt-popover-item ${typeClass} ${isActive ? "active" : ""}`}
                              style={!typeClass ? { background: pt.color + "22", color: pt.color, outline: isActive ? `2px solid ${pt.color}` : "none" } : {}}
                              onClick={() => {
                                setActivePostTypeFilter(isActive ? null : pt.name);
                                setPostTypePopoverOpen(false);
                              }}
                            >
                              {iconMap[pt.icon] || <Tag size={14} />} {pt.name}
                              <span className="pt-count">{count > 9 ? "+10" : String(count).padStart(2, '0')}</span>
                            </div>
                          );
                        })}
                      </div>
                    }
                    trigger="click"
                    placement="bottomLeft"
                    overlayClassName="post-type-popover-overlay"
                    open={postTypePopoverOpen}
                    onOpenChange={setPostTypePopoverOpen}
                  >
                    <div className={`filter-chip ${postTypePopoverOpen || activePostTypeFilter ? "filter-chip-active" : ""}`}>
                      {activePostTypeFilter ? (
                        <>
                          <span className="filter-chip-icon" style={{ maskImage: "url(/assets/images/posttype.svg)", WebkitMaskImage: "url(/assets/images/posttype.svg)" }} /> {activePostTypeFilter}
                          <X size={12} style={{ marginLeft: 4, flexShrink: 0 }} onClick={(e) => { e.stopPropagation(); setActivePostTypeFilter(null); }} />
                        </>
                      ) : (
                        <><span className="filter-chip-icon" style={{ maskImage: "url(/assets/images/posttype.svg)", WebkitMaskImage: "url(/assets/images/posttype.svg)" }} /> Post Type <ChevronDown size={14} /></>
                      )}
                    </div>
                  </Popover>
                  <div style={{ position: "relative" }}>
                    <DatePicker.RangePicker
                      open={datePickerOpen}
                      onOpenChange={(open) => { setDatePickerOpen(open); }}
                      onChange={(dates) => {
                        setSelectedDateRange(dates);
                        if (dates) {
                          setDateRangeSelected(true);
                          const isToday = dates[0].isSame(moment(), 'day') && dates[1].isSame(moment(), 'day');
                          if (isToday) {
                            setFloatingDateLabel("Today's Discussion");
                          } else {
                            setFloatingDateLabel(dates[0].format("MMM DD") + " - " + dates[1].format("MMM DD"));
                          }
                        } else {
                          setDateRangeSelected(false);
                          setFloatingDateLabel("Today's Discussion");
                        }
                      }}
                      dropdownClassName="chat-date-picker-popup"
                      style={{ position: "absolute", opacity: 0, width: 0, height: 0, padding: 0, border: "none", pointerEvents: "none" }}
                      suffixIcon={null}
                      allowClear={false}
                      inputReadOnly
                      ranges={{
                        Today: [moment(), moment()],
                        "Last 7 days": [moment().subtract(7, "d"), moment()],
                        "Last 30 days": [moment().subtract(30, "d"), moment()],
                        "Last 90 days": [moment().subtract(90, "d"), moment()],
                      }}
                    />
                    <div className={`filter-chip ${datePickerOpen || dateRangeSelected ? "filter-chip-active" : ""}`} onClick={() => setDatePickerOpen(true)}>
                      {dateRangeSelected && selectedDateRange ? (
                        <>
                          <span className="filter-chip-icon" style={{ maskImage: "url(/assets/images/date.svg)", WebkitMaskImage: "url(/assets/images/date.svg)" }} /> {selectedDateRange[0].format("MMM D")} – {selectedDateRange[1].format("MMM D")}
                          <X size={12} style={{ marginLeft: 4, flexShrink: 0 }} onClick={(e) => { e.stopPropagation(); clearDateFilter(); }} />
                        </>
                      ) : (
                        <><span className="filter-chip-icon" style={{ maskImage: "url(/assets/images/date.svg)", WebkitMaskImage: "url(/assets/images/date.svg)" }} /> Date <ChevronDown size={14} /></>
                      )}
                    </div>
                  </div>
                  <Popover
                    trigger="click"
                    placement="bottomLeft"
                    overlayClassName="chat-members-popover-overlay"
                    open={chatMemberPopoverOpen}
                    onOpenChange={(v) => { setChatMemberPopoverOpen(v); if (!v) setOpenPermDropdown(null); }}
                    content={
                      <div className="cm-popover">
                        <div className="cm-popover-list">
                          {activeRoom?.members?.length > 0 ? activeRoom.members.map(m => {
                            const isSelf = String(m.id || m._id) === String(currentUserId);
                            return (
                              <div key={m.id || m._id} className="cm-popover-item">
                                <Avatar size={28} src={getImageUrl(m.image)} style={{ background: "#0DDFAB", fontSize: 11, flexShrink: 0, fontWeight: 600 }}>
                                  {m.first_name?.charAt(0)}
                                </Avatar>
                                <div className="cm-popover-name" style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                                  <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {m.first_name} {m.last_name}
                                  </span>
                                  {isSelf && <span className="cm-you-tag">You</span>}
                                </div>
                                {myPermission === "Full edit" && !isSelf && (
                                  <Edit2
                                    size={13}
                                    className="add-people-edit-icon"
                                    style={{ flexShrink: 0 }}
                                    onClick={e => {
                                      e.stopPropagation();
                                      const btnRect = chatMemberBtnRef.current?.getBoundingClientRect();
                                      if (btnRect) {
                                        setChatMemberEditPos({ top: btnRect.bottom + 4, left: btnRect.left });
                                      }
                                      setChatMemberEditId(m.id || m._id);
                                      setChatMemberPopoverOpen(false);
                                    }}
                                  />
                                )}
                              </div>
                            );
                          }) : (
                            <div className="cm-popover-empty">No members yet</div>
                          )}
                        </div>
                        {myPermission === "Full edit" && (
                          <div
                            className="cm-popover-add"
                            onClick={() => { setChatMemberPopoverOpen(false); setShowAddPeoplePanel(true); setAddPeopleSearch(""); setSelectedForInvite([]); ChatService.searchUsers("").then(r => { if (r.Good) setAddPeopleResults(r.users); }).catch(() => {}); }}
                          >
                            <UserPlus size={13} /> Add Member
                          </div>
                        )}
                      </div>
                    }
                  >
                    <div ref={chatMemberBtnRef} className={`filter-chip ${chatMemberPopoverOpen ? "filter-chip-active" : ""}`}>
                      <span className="filter-chip-icon" style={{ maskImage: "url(/assets/images/chatmember.svg)", WebkitMaskImage: "url(/assets/images/chatmember.svg)" }} /> Chat Member <ChevronDown size={14} />
                    </div>
                  </Popover>
                  <div
                    className={`filter-chip ${showAttachmentsPanel ? "filter-chip-active" : ""}`}
                    onClick={() => setShowAttachmentsPanel(p => !p)}
                  >
                    <span className="filter-chip-icon" style={{ maskImage: "url(/assets/images/attachments.svg)", WebkitMaskImage: "url(/assets/images/attachments.svg)" }} /> Attachments <ChevronDown size={14} />
                  </div>
                  {pinnedMessages.length > 0 && (
                    <div
                      className={`filter-chip ${showPinnedPanel ? "filter-chip-active" : ""}`}
                      onClick={() => setShowPinnedPanel(p => !p)}
                    >
                      <Pin size={14} /> Pinned <ChevronDown size={14} />
                    </div>
                  )}

                  {/* Search field — always visible, right-aligned */}
                  <div className={`chat-msgsearch-wrap${msgSearchFocused ? " has-focus" : ""}${msgSearchQuery ? " has-value" : ""}`} style={{ marginLeft: "auto" }}>
                    <input
                      ref={msgSearchRef}
                      className="chat-msgsearch"
                      type="text"
                      value={msgSearchQuery}
                      onChange={e => setMsgSearchQuery(e.target.value)}
                      onFocus={() => setMsgSearchFocused(true)}
                      onBlur={() => setMsgSearchFocused(false)}
                    />
                    <label className="chat-msgsearch-label">Search messages...</label>
                    {msgSearchQuery && (
                      <i className="uil-times chat-msgsearch-clear" onClick={() => setMsgSearchQuery("")} />
                    )}
                  </div>
                </div>
                )}
              </div>

              {/* Channel Body + Attachments Panel */}
              <div style={{ flex: 1, display: "flex", overflow: "hidden", position: 'relative', zIndex: 1 }}>
              <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "visible" }}>
                {msgLoading ? (
                  /* Loading: show shimmer */
                  <div style={{ flex: 1 }}><ChatShimmer /></div>
                ) : messages.length === 0 ? (
                  /* No messages: vertically centered welcome */
                  <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <div className="channel-welcome-banner" style={{ border: "none", marginBottom: 0 }}>
                      <img src="/assets/images/Mask group.svg" alt="team chat" style={{ width: 52, height: 52, marginBottom: 8 }} />
                      <div className="channel-welcome-banner-title">Team Chat in #{getRoomDisplayName(activeRoom)}</div>
                      <div className="channel-welcome-banner-desc">
                        Bring your team together with real-time conversations. Link chats to tasks and never miss an important update.
                      </div>
                      {myPermission === "Full edit" && (
                        <button className="welcome-add-btn" onClick={() => { setShowAddPeoplePanel(true); setAddPeopleSearch(""); setSelectedForInvite([]); ChatService.searchUsers("").then(r => { if (r.Good) setAddPeopleResults(r.users); }).catch(() => {}); }}>
                          <UserPlus size={15} /> Add People
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ flex: 1, overflowY: "auto", padding: "0 24px 24px", opacity: 1 }} ref={scrollRef} onScroll={handleScroll}>
                      <div style={{ position: "sticky", top: 0, zIndex: 10, background: "rgba(255,255,255,0.9)", backdropFilter: "blur(4px)", padding: "8px 0", display: "flex", alignItems: "center", margin: "0 0 8px", gap: "12px" }}>
                        <div style={{ flex: 1, height: "1px", background: "#ebebeb" }} />
                        <div className="today-discussion-pill">
                          {floatingDateLabel} <ChevronDown size={14} />
                        </div>
                        <div style={{ flex: 1, height: "1px", background: "#ebebeb" }} />
                      </div>

                      <div className="channel-welcome-banner" style={{ padding: '12px 0', marginBottom: 10 }}>
                        <div className="channel-welcome-banner-title">Team Chat in #{getRoomDisplayName(activeRoom)}</div>
                        <div className="channel-welcome-banner-desc">
                          Bring your team together with real-time conversations. Link chats to tasks and never miss an important update.
                        </div>
                        {myPermission === "Full edit" && (
                          <button className="welcome-add-btn" onClick={() => { setShowAddPeoplePanel(true); setAddPeopleSearch(""); setSelectedForInvite([]); ChatService.searchUsers("").then(r => { if (r.Good) setAddPeopleResults(r.users); }).catch(() => {}); }}>
                            <UserPlus size={15} /> Add People
                          </button>
                        )}
                      </div>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                  {(() => {
                    let filtered = selectedDateRange
                      ? messages.filter(m => moment(m.createdAt).isBetween(selectedDateRange[0], selectedDateRange[1], 'day', '[]'))
                      : messages;
                    if (activePostTypeFilter) {
                      filtered = filtered.filter(m => (m.postType || m.type) === activePostTypeFilter);
                    }
                    if (msgSearchQuery.trim()) {
                      const q = msgSearchQuery.toLowerCase();
                      filtered = filtered.filter(m =>
                        (m.message || m.content || m.text || "").toLowerCase().includes(q) ||
                        (m.senderName || m.sender_name || "").toLowerCase().includes(q)
                      );
                    }

                    if (filtered.length === 0 && (activePostTypeFilter || selectedDateRange)) {
                      return (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 20px", color: "#9aa0a6" }}>
                          <LayoutGrid size={40} style={{ opacity: 0.2, marginBottom: 12 }} />
                          <div style={{ fontWeight: 500, marginBottom: 4 }}>No messages found</div>
                          <div style={{ fontSize: 13 }}>No {activePostTypeFilter || "messages"} match the current filters.</div>
                        </div>
                      );
                    }

                    let lastDate = null;
                    return filtered.map((msg, idx) => {
                      const msgDate = moment(msg.createdAt).format("YYYY-MM-DD");
                      const showDivider = msgDate !== lastDate;
                      lastDate = msgDate;
                      const channelDateLabel = (() => {
                        const d = new Date(msg.createdAt);
                        const today = new Date();
                        if (d.toDateString() === today.toDateString()) return "Today's Discussion";
                        return d.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' });
                      })();

                      const isOwn = String(msg.sender_id) === String(currentUserId);
                      const time = new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                      const prevMsg = filtered[idx - 1];
                      const nextMsg = filtered[idx + 1];
                      const showTime = showDivider || !prevMsg || String(prevMsg.sender_id) !== String(msg.sender_id) || moment(prevMsg.createdAt).format("HH:mm") !== moment(msg.createdAt).format("HH:mm");
                      const nextIsGrouped = nextMsg && String(nextMsg.sender_id) === String(msg.sender_id) && moment(nextMsg.createdAt).format("HH:mm") === moment(msg.createdAt).format("HH:mm");
                      const initials = msg.sender_name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || "?";

                    const hasPostType = msg.postType || msg.type === "announcement" || msg.type === "discussion" || msg.type === "idea" || msg.type === "update";
                    const postType = msg.postType || msg.type;
                    const lines = msg.text?.split('\n') || [];
                    const title = hasPostType ? lines[0] : null;
                    const body = hasPostType ? lines.slice(1).join('\n') : msg.text;

                    const postTypeclassMap = { Announcement: "announcement", Discussion: "discussion", Idea: "idea", Update: "update" };
                    const customIconMap = { Megaphone: <Megaphone size={12} />, MessagesSquare: <MessagesSquare size={12} />, Lightbulb: <Lightbulb size={12} />, Bell: <Bell size={12} />, Tag: <Tag size={12} />, Star: <Star size={12} />, Zap: <Zap size={12} />, Heart: <Heart size={12} />, Flag: <Flag size={12} />, Bookmark: <Bookmark size={12} />, AlertCircle: <AlertCircle size={12} />, CheckCircle: <CheckCircle size={12} /> };
                    const getPostTypeIcon = (type) => {
                      switch(type?.toLowerCase()) {
                        case 'announcement': return <Megaphone size={12} />;
                        case 'discussion': return <MessagesSquare size={12} />;
                        case 'idea': return <Lightbulb size={12} />;
                        case 'update': return <Bell size={12} />;
                        default: {
                          const customPt = customPostTypes.find(pt => pt.name === type || pt.name?.toLowerCase() === type?.toLowerCase());
                          return customIconMap[customPt?.icon] || <Tag size={12} />;
                        }
                      }
                    };

                      return (
                        <React.Fragment key={msg._stableKey || msg._id || msg.id || idx}>
                          {showDivider && (
                            <div className="chat-date-divider" data-date-label={channelDateLabel} style={{ height: 0, overflow: 'hidden', margin: 0 }} />
                          )}
                          <div id={`msg-${String(msg._id || msg.id)}`} className={`modern-bubble-row ${isOwn ? "own" : ""}${!showTime ? " grouped" : ""}${showTime && nextIsGrouped ? " grouped-start" : ""}`}>
                            {showTime ? (
                              <Avatar
                                size={26}
                                src={msg.sender_image ? getImageUrl(msg.sender_image) : null}
                                style={{ background: "#0DDFAB", color: "#fff", fontSize: 10, flexShrink: 0, marginTop: '4px' }}
                              >
                                {initials}
                              </Avatar>
                            ) : null}
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: isOwn ? 'flex-end' : 'flex-start' }}>
                              {/* ── Meta row outside bubble: name (channel) · forwarded/edited · time (first in group only) ── */}
                              {(showTime || msg.is_forwarded || msg.is_edited) && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 3, width: '100%', justifyContent: isOwn ? 'flex-end' : 'flex-start', flexWrap: 'wrap' }}>
                                {activeRoom?.type !== "direct" && showTime && (
                                  <span className="modern-bubble-info" style={{ marginBottom: 0, fontSize: 11 }}>{isOwn ? "You" : msg.sender_name}</span>
                                )}
                                {msg.is_forwarded && (
                                  <span style={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: 10, color: '#8696a0', fontStyle: 'italic' }}>
                                    <CornerUpRight size={9} strokeWidth={2} />Forwarded
                                  </span>
                                )}
                                {msg.is_edited && !msg.is_forwarded && (
                                  <span style={{ fontSize: 10, color: '#8696a0', fontStyle: 'italic' }}>Edited</span>
                                )}
                                {showTime && (
                                  <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                    {isOwn && (
                                      msg.isFailed ? <AlertCircle size={11} style={{ color: '#ff4d4f' }} /> :
                                      msg.isSending ? <Clock size={11} className="tick-sending" /> :
                                      isMsgRead(msg) ? <CheckCheck size={12} className="tick-read" /> :
                                      msg.isDelivered ? <CheckCheck size={12} className="tick-delivered" /> :
                                      <Check size={12} className="tick-sent" />
                                    )}
                                    <span style={{ fontSize: 10, color: '#9aa0a6' }}>{moment(msg.createdAt).format("h:mm A")}</span>
                                  </span>
                                )}
                              </div>
                              )}
                              <div className="modern-bubble" style={{ opacity: msg.isSending ? 0.6 : 1 }}>
                            {msg.reply_to && (
                              <div className="bubble-reply-reference" onClick={() => scrollToMessage(msg.reply_to)}>
                                {(() => {
                                  const parent = messages.find(m => (m._id || m.id) === msg.reply_to);
                                  return (
                                    <>
                                      <div className="reply-ref-name">{parent?.sender_name || "Original Message"}</div>
                                      <div className="reply-ref-text">{getAttachmentPreview(parent) || "Message"}</div>
                                    </>
                                  );
                                })()}
                              </div>
                            )}
                            {msg.is_pinned && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#1a73e8', marginBottom: '6px' }}>
                                <Pin size={10} fill="#1a73e8" /> <span>Pinned</span>
                              </div>
                            )}
                            {hasPostType && (() => {
                              const lowerPt = (postType || '').toLowerCase();
                              const defaultCssMap = { announcement: 'announcement', discussion: 'discussion', idea: 'idea', update: 'update' };
                              const cssClass = defaultCssMap[lowerPt];
                              if (cssClass) {
                                return (
                                  <div className={`bubble-post-badge ${cssClass}`}>
                                    {getPostTypeIcon(postType)} {postType}
                                  </div>
                                );
                              }
                              const customPt = customPostTypes.find(pt => pt.name === postType || (pt.name || '').toLowerCase() === lowerPt);
                              const color = customPt?.color || "#6366f1";
                              const r = parseInt(color.slice(1, 3), 16) || 99;
                              const g = parseInt(color.slice(3, 5), 16) || 102;
                              const b = parseInt(color.slice(5, 7), 16) || 241;
                              return (
                                <span style={{
                                  display: "inline-flex", alignItems: "center", gap: "4px",
                                  padding: "3px 8px", borderRadius: "6px", fontSize: "11px",
                                  fontWeight: "600", marginBottom: "6px", lineHeight: "1.4",
                                  background: `rgba(${r},${g},${b},0.25)`,
                                  color: color,
                                }}>
                                  {getPostTypeIcon(postType)} {postType}
                                </span>
                              );
                            })()}
                            {title && <div className="modern-bubble-text" style={{ marginBottom: '2px' }}>{renderMessageText(title)}</div>}
                            <div className="modern-bubble-text">{renderMessageText(body)}</div>
                            {msg.attachments?.length > 0 && (() => {
                              const baseUrl = (process.env.REACT_APP_BASE_URL || "http://localhost:5026/api/v1").split("/api/v1")[0];
                              const getUrl = (f) => f.isLocal ? f.url : `${baseUrl}${f.url?.startsWith("/public") ? f.url : `/public${f.url}`}`;
                              const imgFiles   = msg.attachments.filter(f => f.fileType === "image" || /\.(jpg|jpeg|png|gif|webp)$/i.test(f.url || ""));
                              const otherFiles = msg.attachments.filter(f => !(f.fileType === "image" || /\.(jpg|jpeg|png|gif|webp)$/i.test(f.url || "")));
                              const cols = imgFiles.length === 1 ? 1 : imgFiles.length === 2 ? 2 : 3;
                              return (
                                <>
                                  {imgFiles.length > 0 && (
                                    <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 3, marginTop: 6, maxWidth: cols === 1 ? 200 : cols === 2 ? 244 : 270, borderRadius: 8, overflow: "hidden" }}>
                                      {imgFiles.map((f, i) => (
                                        <img key={i} src={getUrl(f)} alt="" style={{ width: "100%", aspectRatio: "1/1", objectFit: "cover", display: "block", cursor: "pointer" }} onClick={() => window.open(getUrl(f), "_blank")} />
                                      ))}
                                    </div>
                                  )}
                                  {otherFiles.map((file, i) => {
                                    const fileUrl = getUrl(file);
                                    const isAudio = file.fileType === "audio" || /\.(mp3|wav|webm|m4a|ogg)$/i.test(file.url || "");
                                    if (isAudio) return (
                                      <div key={i} className="voice-note-bubble" style={{ marginTop: 8 }}>
                                        <audio controls src={fileUrl} style={{ width: '220px', height: '30px' }} />
                                      </div>
                                    );
                                    const ext = (file.name?.split('.').pop() || '').toLowerCase();
                                    const iconBg = ext === 'pdf' ? '#ff4d4f' : ['doc','docx'].includes(ext) ? '#1a73e8' : ['xls','xlsx','csv'].includes(ext) ? '#0f9d58' : ['ppt','pptx'].includes(ext) ? '#ff6d00' : ['zip','rar','7z'].includes(ext) ? '#9c27b0' : '#5f6368';
                                    return (
                                      <a key={i} href={fileUrl} onClick={async (e) => { e.preventDefault(); try { const res = await fetch(fileUrl); const blob = await res.blob(); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = file.name || 'file'; a.click(); URL.revokeObjectURL(url); } catch { window.open(fileUrl, '_blank'); } }} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: 8, padding: '8px 12px', background: 'rgba(0,0,0,0.04)', borderRadius: '10px', textDecoration: 'none', border: '1px solid rgba(0,0,0,0.08)', maxWidth: '240px', cursor: 'pointer' }}>
                                        <div style={{ width: 36, height: 36, borderRadius: '8px', background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                          <FileText size={18} color="#fff" />
                                        </div>
                                        <div style={{ flex: 1, overflow: 'hidden' }}>
                                          <div style={{ fontSize: '12px', fontWeight: '600', color: '#1d1d1d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name || "File"}</div>
                                          <div style={{ fontSize: '11px', color: '#5f6368', marginTop: '2px' }}>{ext ? ext.toUpperCase() : 'FILE'}</div>
                                        </div>
                                        <i className="uil-import" style={{ fontSize: '16px', color: '#5f6368', flexShrink: 0 }} />
                                      </a>
                                    );
                                  })}
                                </>
                              );
                            })()}
                            {msg.reactions?.length > 0 && (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px' }}>
                                {msg.reactions.map((r, i) => (
                                  <Tooltip key={i} title={getReactionTooltip(r.users)} placement="top">
                                    <div className="reaction-badge" onClick={() => handleToggleReaction(msg._id, r.emoji)} style={{
                                      background: r.users.includes(currentUserId) ? '#e0faf3' : '#f0f0f0',
                                      border: r.users.includes(currentUserId) ? '1px solid #0DDFAB' : '1px solid #e0e0e0',
                                      borderRadius: '12px',
                                      padding: '2px 6px',
                                      fontSize: '11px',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '4px'
                                    }}>
                                      <span>{r.emoji}</span>
                                      <span style={{ color: '#5f6368' }}>{r.users.length}</span>
                                    </div>
                                  </Tooltip>
                                ))}
                              </div>
                            )}
                          </div>
                          {!(msg.isSending || msg.isFailed) && myPermission !== "View Only" && <div className={`modern-bubble-actions${openReactionPopoverId === msg._id ? ' reaction-open' : ''}`} style={isOwn ? { marginRight: 44 } : { marginLeft: 44 }}>
                            <span className="action-tip" data-tip="Like"><FaThumbsUp size={12} className="modern-action-icon" onClick={() => handleToggleReaction(msg._id, '👍')} /></span>
                            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                <span className="action-tip" data-tip="Add Reaction"><FaSmile size={12} className="modern-action-icon" onClick={(e) => { const rect = e.currentTarget.getBoundingClientRect(); setReactionPickerDir({ v: window.innerHeight - rect.bottom < 390 ? 'up' : 'down', h: rect.left + rect.width / 2 < window.innerWidth / 2 ? 'left' : 'right' }); setOpenReactionPopoverId(openReactionPopoverId === msg._id ? null : msg._id); }} /></span>
                                {openReactionPopoverId === msg._id && (
                                  <div className="reaction-picker-wrapper" style={{ position: 'absolute', ...(reactionPickerDir.v === 'up' ? { bottom: '30px' } : { top: '30px' }), ...(reactionPickerDir.h === 'right' ? { right: 0 } : { left: 0 }), zIndex: 100, background: '#fff', borderRadius: '12px', boxShadow: '0 8px 30px rgba(0,0,0,0.15)', padding: '12px' }} onClick={e => e.stopPropagation()}>
                                    <div className="float-input-wrap float-input-icon" style={{ marginBottom: '4px' }}>
                                      <i className="uil-search" style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', color: '#aaa', fontSize: '15px', pointerEvents: 'none', zIndex: 2 }} />
                                      <input type="text" placeholder=" " className="form-control-custom-focus" style={{ padding: '0 38px 0 14px', borderRadius: '8px', height: '38px', border: '1.5px solid #d0d0d0', lineHeight: '38px', width: '100%' }} onChange={e => { const val = e.target.value; const root = e.currentTarget.closest('.reaction-picker-wrapper'); const inp = root?.querySelector('input:not(.form-control-custom-focus)'); if (inp) { const s = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set; s.call(inp, val); inp.dispatchEvent(new Event('input', { bubbles: true })); inp.dispatchEvent(new Event('change', { bubbles: true })); } }} />
                                      <label style={{ fontSize: '13px', left: '14px' }}>Search emojis...</label>
                                    </div>
                                    <EmojiPicker
                                        onEmojiClick={(emojiData) => { handleToggleReaction(msg._id, emojiData.emoji); setOpenReactionPopoverId(null); }}
                                        skinTonesDisabled
                                        previewConfig={{ showPreview: false }}
                                        width={350}
                                        height={300}
                                    />
                                  </div>
                                )}
                            </div>
                            <span className="action-tip" data-tip={msg.is_pinned ? "Unpin" : "Pin"}><FaThumbtack size={12} className="modern-action-icon" onClick={() => handleTogglePin(msg._id)} style={{ color: msg.is_pinned ? '#1a73e8' : '' }} /></span>
                            <span className="action-tip" data-tip="Reply"><FaReply size={12} className="modern-action-icon" onClick={() => setReplyTo(msg._id || msg.id)} /></span>
                            <span className="action-tip" data-tip="Forward"><FaShare size={12} className="modern-action-icon" onClick={() => { setForwardMsg(msg); setShowForwardModal(true); }} /></span>
                            {isOwn && !isVoiceNote(msg) && isWithinOneHour(msg) && <span className="action-tip" data-tip="Edit"><FaPen size={12} className="modern-action-icon" onClick={() => { setEditingMessage(msg); setEditingAttachments(msg.attachments || []); setDmText(msg.text || ""); setSelectedPostType(msg.postType || msg.type); }} /></span>}
                            {isOwn && isWithinOneHour(msg) && <span className="action-tip" data-tip="Delete"><FaTrash size={12} className="modern-action-icon" onClick={() => { setMessageToDelete(msg._id || msg.id); setDeleteFor("everyone"); document.getElementById("openChatMsgDeleteModal").click(); }} style={{ color: '#ff4d4f' }} /></span>}
                          </div>}
                        </div>
                      </div>
                        </React.Fragment>
                      );
                    });
                  })()}
                </div>
              </div>
                  </>
                )}
              </div>

              {/* Attachments panel — sits alongside messages only */}
              {showAttachmentsPanel && (() => {
                const baseUrl = (process.env.REACT_APP_BASE_URL || "http://localhost:5026/api/v1").split("/api/v1")[0];
                const allAttachments = messages.flatMap(m => (m.attachments || []).map(a => ({ ...a, sender_name: m.sender_name, createdAt: m.createdAt })));
                const images = allAttachments.filter(a => a.fileType === "image" || /\.(jpg|jpeg|png|gif|webp)$/i.test(a.url));
                const videos = allAttachments.filter(a => a.fileType === "video" || /\.(mp4|mov|avi|webm)$/i.test(a.url));
                const docs   = allAttachments.filter(a => !images.includes(a) && !videos.includes(a) && !/^https?:\/\//i.test(a.url));
                const links  = messages.flatMap(m => {
                  const matches = (m.text || "").match(/https?:\/\/[^\s]+/g) || [];
                  return matches.map(url => ({ url, sender_name: m.sender_name, createdAt: m.createdAt }));
                });
                const current = ({ Images: images, Videos: videos, Docs: docs, Links: links })[attachmentsTab] || [];
                const getUrl = (a) => { const norm = a.url?.startsWith("/public") ? a.url : `/public${a.url}`; return `${baseUrl}${norm}`; };
                return (
                  <div className="attachments-panel">
                    <div className="attachments-tabs">
                      {[
                        { label: "Images", icon: <Image size={12} /> },
                        { label: "Videos", icon: <Video size={12} /> },
                        { label: "Docs",   icon: <FileText size={12} /> },
                        { label: "Links",  icon: <Link2 size={12} /> },
                      ].map(({ label, icon }) => (
                        <div key={label} className={`attachments-tab ${attachmentsTab === label ? "active" : ""}`} onClick={() => setAttachmentsTab(label)}>
                          {icon} {label}
                        </div>
                      ))}
                    </div>
                    <div className="attachments-content">
                      {current.length === 0 ? (
                        <div style={{ textAlign: "center", color: "#9e9e9e", fontSize: 12, marginTop: 40 }}>No {attachmentsTab.toLowerCase()} yet</div>
                      ) : attachmentsTab === "Images" ? (
                        <div className="attachments-grid">{current.map((a, i) => <img key={i} src={getUrl(a)} alt="" className="attachment-thumb" onClick={() => window.open(getUrl(a), "_blank")} />)}</div>
                      ) : attachmentsTab === "Videos" ? (
                        <div className="attachments-grid">{current.map((a, i) => <video key={i} src={getUrl(a)} className="attachment-thumb" controls />)}</div>
                      ) : attachmentsTab === "Links" ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{current.map((l, i) => <a key={i} href={l.url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: "#1a73e8", wordBreak: "break-all", padding: "6px 0", borderBottom: "1px solid #f0f0f0" }}>{l.url}</a>)}</div>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{current.map((a, i) => <a key={i} href={getUrl(a)} target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px", borderRadius: 8, background: "#f8f9fa", fontSize: 12, color: "#1d1d1d" }}><Paperclip size={14} />{a.name || "Document"}</a>)}</div>
                      )}
                    </div>
                  </div>
                );
              })()}
              {/* Add People panel — flex sibling to messages, stays above footer */}
              {showAddPeoplePanel && (
                <div className="add-people-panel">
                  <div className="add-people-panel-header">
                    <span>Add People</span>
                    <div style={{ cursor: "pointer" }} onClick={() => setShowAddPeoplePanel(false)}>
                      <X size={16} color="#5f6368" />
                    </div>
                  </div>
                  <div className="add-people-search-row">
                    <div className="float-input-wrap float-input-icon" style={{ flex: 1, margin: 0 }}>
                      <i className="uil-search" style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", color: "#aaa", fontSize: "15px", pointerEvents: "none", zIndex: 2 }} />
                      <input
                        type="text"
                        autoComplete="new-password"
                        data-form-type="other"
                        placeholder=" "
                        value={addPeopleSearch}
                        onChange={e => handleAddPeopleSearch(e.target.value)}
                        autoFocus
                        style={{ padding: "0 36px 0 14px", borderRadius: "5px", height: "36px", border: "1.5px solid #d0d0d0", lineHeight: "36px", margin: 0, width: "100%" }}
                        className="form-control-custom-focus"
                      />
                      <label style={{ fontSize: "13px", left: "14px" }}>Search people</label>
                    </div>
                    <button className="add-people-invite-btn" onClick={() => {
                      const selected = selectedForInvite.map(id => selectedUsersMap[id] || addPeopleResults.find(u => u.id === id)).filter(Boolean);
                      setShareEmail(selected.map(u => u.email).filter(Boolean).join(", "));
                      setShowShareModal(true);
                      // Preload invite link in background so it's ready when user clicks Generate Link
                      if (!inviteLink && activeRoom) {
                        const permAtClick = inviteAccess;
                        ChatService.generateInviteLink({ roomId: activeRoom._id, permission: permAtClick })
                          .then(r => { if (r.Good) setInviteAccess(p => { if (p === permAtClick) setInviteLink(buildInviteLink(r.inviteLink)); return p; }); })
                          .catch(() => {});
                      }
                    }} disabled={selectedForInvite.length === 0}>
                      Invite
                    </button>
                  </div>
                  {/* Selected users — always visible below search field */}
                  {selectedForInvite.length > 0 && (
                    <div style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: 4 }}>
                      {selectedForInvite.map(id => {
                        const u = selectedUsersMap[id] || addPeopleResults.find(r => r.id === id);
                        if (!u) return null;
                        return (
                          <div key={id} className="add-people-item selected" onClick={() => toggleInviteUser(u)}>
                            <Avatar size={30} src={getImageUrl(u.image)} style={{ background: "#0DDFAB", fontSize: 12, flexShrink: 0 }}>
                              {u.first_name?.charAt(0)}
                            </Avatar>
                            <span className="add-people-name">{u.first_name} {u.last_name}</span>
                            <X size={14} style={{ color: '#9e9e9e', flexShrink: 0, cursor: 'pointer' }} />
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className="add-people-list">
                    {addPeopleResults.map(u => {
                      const isMember = activeRoom?.members?.some(m => String(m.id || m._id) === String(u.id));
                      const isSelected = selectedForInvite.includes(u.id);
                      return (
                        <div
                          key={u.id}
                          className={`add-people-item ${isMember ? "disabled" : ""} ${isSelected ? "selected" : ""}`}
                          onClick={() => !isMember && toggleInviteUser(u)}
                        >
                          <Avatar size={30} src={getImageUrl(u.image)} style={{ background: "#0DDFAB", fontSize: 12, flexShrink: 0 }}>
                            {u.first_name?.charAt(0)}
                          </Avatar>
                          <span className="add-people-name">{u.first_name} {u.last_name}</span>
                          <Edit2 size={14} className="add-people-edit-icon" onClick={e => { e.stopPropagation(); setShowShareModal(true); setOpenPermDropdown(null); if (!inviteLink && activeRoom) { ChatService.generateInviteLink({ roomId: activeRoom._id, permission: inviteAccess }).then(r => { if (r.Good) setInviteLink(buildInviteLink(r.inviteLink)); }).catch(() => {}); } }} />
                          {isSelected && <div className="add-people-check">✓</div>}
                          {isMember && <span className="add-people-member-tag">Added</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {/* Pinned Messages panel */}
              {showPinnedPanel && (
                <div className="attachments-panel">
                  <div style={{ padding: '12px 16px', fontWeight: 600, fontSize: 13, borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Pin size={14} /> Pinned Messages</span>
                    <X size={14} style={{ cursor: 'pointer', color: '#9e9e9e' }} onClick={() => setShowPinnedPanel(false)} />
                  </div>
                  <div className="attachments-content" style={{ padding: '8px 0' }}>
                    {pinnedMessages.length === 0 ? (
                      <div style={{ textAlign: 'center', color: '#9e9e9e', fontSize: 12, marginTop: 40 }}>No pinned messages</div>
                    ) : pinnedMessages.map((msg, i) => (
                      <div key={msg._id || i} style={{ padding: '10px 16px', borderBottom: '1px solid #f8f8f8' }}>
                        <div style={{ fontWeight: 600, fontSize: 11, color: '#1a73e8', marginBottom: 3 }}>{msg.sender_name}</div>
                        <div style={{ fontSize: 12, color: '#3c4043', lineHeight: 1.5, wordBreak: 'break-word' }}>{msg.text || "📎 Attachment"}</div>
                        <div style={{ fontSize: 10, color: '#9e9e9e', marginTop: 4 }}>{moment(msg.createdAt).format("MMM D, h:mm a")}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              </div>{/* end messages+panel flex row */}

              {/* Footer — always full width */}
              {myPermission === "View Only" ? (
                <div style={{ padding: "14px 24px", background: "#f8f9fa", borderTop: "1px solid #ebebeb", textAlign: "center", fontSize: "13px", color: "#9e9e9e" }}>
                  You have view-only access to this channel.
                </div>
              ) : (
              <div className="post-topic-container" style={{ position: 'relative', overflow: 'visible', zIndex: 100 }} onPaste={handleDmPaste}>
                {!editingMessage && dmAttachments.length > 0 && (
                  <div className="d-flex flex-wrap" style={{ gap: '8px', padding: '10px 20px', borderBottom: '1px solid #eee' }}>
                    {dmAttachments.map((file, i) => (
                      <div key={i} title={file.name} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#1D1D1D', borderRadius: '4px', padding: '0 8px', height: '24px', fontSize: '11px', color: '#0DDFAB', maxWidth: '200px', fontWeight: 500 }}>
                        <i className="uil-import" style={{ fontSize: '11px', flexShrink: 0 }} />
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{file.name}</span>
                        <span onClick={() => setDmAttachments(dmAttachments.filter((_, idx) => idx !== i))} style={{ flexShrink: 0, cursor: 'pointer', fontSize: '13px', lineHeight: 1, marginLeft: '2px' }}>×</span>
                      </div>
                    ))}
                  </div>
                )}
                {replyTo && (
                  <div className="reply-preview">
                    <div className="reply-preview-content">
                      <span className="reply-label">Replying to:</span>
                      <span className="reply-text">{getAttachmentPreview(messages.find(m => (m._id || m.id) === (replyTo?._id || replyTo?.id || replyTo))) || "Message"}</span>
                    </div>
                    <X size={14} className="close-reply" onClick={() => setReplyTo(null)} />
                  </div>
                )}
                {editingMessage && (
                  <>
                    <div className="edit-preview">
                      <span className="edit-label">Editing Message</span>
                      <X size={14} className="close-edit" onClick={() => { setEditingMessage(null); setEditingAttachments([]); setDmAttachments([]); setDmText(""); }} />
                    </div>
                    {renderEditAttachmentPills()}
                  </>
                )}
                {selectedPostType && <div className="post-topic-label">{selectedPostType}</div>}
                <MentionsInput
                  className="dm-footer-input"
                  style={mentionInputStyle}
                  placeholder={selectedPostType ? `Share an ${selectedPostType.toLowerCase()}...` : "Type anything..."}
                  value={dmText}
                  onChange={(e, newValue) => { setDmText(newValue); handleTyping(true); }}
                  onBlur={() => handleTyping(false)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (isRecording) {
                        shouldSendVoiceNote.current = true;
                        stopRecording();
                        return;
                      }
                      if (dmText.trim() || dmAttachments.length > 0) {
                        handleSendMessage({ text: dmText, postType: selectedPostType, attachments: dmAttachments });
                        setDmText("");
                        setDmAttachments([]);
                        setReplyTo(null);
                        setEditingMessage(null);
                        setShowDmEmojiPicker(false);
                      }
                    }
                  }}
                  allowSpaceInQuery
                >
                  <Mention
                    trigger="@"
                    data={channelMentionUsers}
                    style={{ backgroundColor: '#0DDFAB', color: '#0DDFAB', borderRadius: 3 }}
                    renderSuggestion={(entry) => (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#1d1d1d' }}>
                        <Avatar size={24} style={{ background: '#0DDFAB', color: '#fff', fontSize: 10, flexShrink: 0 }}>
                          {entry.display?.charAt(0)}
                        </Avatar>
                        <span style={{ fontSize: 13, color: '#1d1d1d', fontWeight: 500 }}>{entry.display}</span>
                      </div>
                    )}
                    displayTransform={(id, display) => `@${display}`}
                    appendSpaceOnAdd
                  />
                </MentionsInput>
                <div className="input-toolbar-modern">
                  <div className="toolbar-left-icons">
                    {!isRecording ? (
                      <>
                        <div className="toolbar-icon-box" onClick={() => dmFileInputRef.current.click()}><Plus size={16} strokeWidth={2.5} /></div>
                        <div className="toolbar-icon-box" onClick={() => setDmText(prev => prev + "@")}><AtSign size={15} strokeWidth={2.5} /></div>
                        <div className="toolbar-icon-box" style={{ position: 'relative' }} onClick={() => setShowDmEmojiPicker(!showDmEmojiPicker)}>
                          <span className="emoji-mask-icon" style={{ maskImage: "url(/assets/images/emojie.svg)", WebkitMaskImage: "url(/assets/images/emojie.svg)", backgroundColor: showDmEmojiPicker ? "#0DDFAB" : "currentColor" }} />
                          {showDmEmojiPicker && (
                            <>
                              <div style={{ position: 'fixed', inset: 0, zIndex: 999, cursor: 'default' }} onClick={() => setShowDmEmojiPicker(false)} />
                              <div style={{ position: 'absolute', bottom: '40px', left: 0, zIndex: 1000, background: '#fff', borderRadius: '12px', boxShadow: '0 8px 30px rgba(0,0,0,0.15)', padding: '12px' }} onClick={e => e.stopPropagation()}>
                                <div className="float-input-wrap float-input-icon" style={{ marginBottom: "12px" }}>
                                  <i className="uil-search" style={{ position: "absolute", right: "14px", top: "50%", transform: "translateY(-50%)", color: "#aaa", fontSize: "15px", pointerEvents: "none", zIndex: 2 }} />
                                  <input
                                    type="text"
                                    autoComplete="new-password"
                                    data-form-type="other"
                                    placeholder=" "
                                    className="form-control-custom-focus"
                                    style={{ padding: "0 38px 0 14px", borderRadius: "8px", height: "38px", border: "1.5px solid #d0d0d0", lineHeight: "38px", width: "100%" }}
                                    onChange={e => {
                                      const val = e.target.value;
                                      const pickerRoot = e.currentTarget.closest('div[style*="z-index: 1000"]');
                                      const internalInput = pickerRoot?.querySelector('input:not(.form-control-custom-focus)');
                                      if (internalInput) {
                                        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value").set;
                                        nativeInputValueSetter.call(internalInput, val);
                                        internalInput.dispatchEvent(new Event('input', { bubbles: true }));
                                        internalInput.dispatchEvent(new Event('change', { bubbles: true }));
                                      }
                                    }}
                                  />
                                  <label style={{ fontSize: "13px", left: "14px" }}>Search emojis...</label>
                                </div>
                                <EmojiPicker
                                  onEmojiClick={(emojiData) => {
                                    setDmText(prev => prev + emojiData.emoji);
                                  }}
                                  skinTonesDisabled
                                  height={400}
                                  width={350}
                                />
                              </div>
                            </>
                          )}
                        </div>
                        <div className="toolbar-icon-box" onClick={startRecording}><span className="audio-mask-icon" style={{ maskImage: "url(/assets/images/audio.svg)", WebkitMaskImage: "url(/assets/images/audio.svg)" }} /></div>

                      </>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#ff4d4f', fontWeight: '600', fontSize: '13px' }}>
                        <div className={`recording-dot ${isPaused ? 'paused' : ''}`} /> Recording {formatTime(recordingTime)}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '8px' }}>
                          {isPaused ? (
                            <Play size={14} style={{ cursor: 'pointer', color: '#0DDFAB' }} onClick={resumeRecording} />
                          ) : (
                            <Pause size={14} style={{ cursor: 'pointer', color: '#1a73e8' }} onClick={pauseRecording} />
                          )}
                          <span style={{ color: '#5f6368', cursor: 'pointer', fontSize: '12px' }} onClick={() => { setIsRecording(false); setIsPaused(false); clearInterval(recordingIntervalRef.current); mediaRecorderRef.current.onstop = null; mediaRecorderRef.current.stop(); mediaStreamRef.current?.getTracks().forEach(t => t.stop()); }}>Cancel</span>
                        </div>
                        <div className="toolbar-icon-box send-recording" onClick={() => { shouldSendVoiceNote.current = true; stopRecording(); }} style={{ background: '#0DDFAB', color: 'white', borderRadius: '50%', width: '28px', height: '28px' }}>
                          <SendHorizontal size={14} />
                        </div>
                      </div>
                    )}
                    
                    {!isRecording && activeRoom?.type === 'channel' && (myPermission === "Full edit" || myPermission === "Edit") && (
                      <div className="post-type-selector">
                        <span className="post-type-label-text">Post Type</span>
                        {customPostTypes.map(pt => {
                          const iconMap = { Megaphone: <Megaphone size={13} />, MessagesSquare: <MessagesSquare size={13} />, Lightbulb: <Lightbulb size={13} />, Bell: <Bell size={13} />, Tag: <Tag size={13} />, Star: <Star size={13} />, Zap: <Zap size={13} />, Heart: <Heart size={13} />, Flag: <Flag size={13} />, Bookmark: <Bookmark size={13} />, AlertCircle: <AlertCircle size={13} />, CheckCircle: <CheckCircle size={13} /> };
                          const classMap = { Announcement: "announcement", Discussion: "discussion", Idea: "idea", Update: "update" };
                          const typeClass = classMap[pt.name] || "";
                          const isActive = selectedPostType === pt.name;
                          const customStyle = !typeClass ? { background: pt.color + "22", color: pt.color, borderColor: isActive ? pt.color : "transparent" } : {};
                          return (
                            <div key={pt.name} className={`post-type-btn ${typeClass} ${isActive ? 'active' : ''}`} style={customStyle} onClick={() => setSelectedPostType(isActive ? null : pt.name)}>
                              {iconMap[pt.icon] || <Tag size={13} />} {pt.name}
                            </div>
                          );
                        })}
                        {myPermission === "Full edit" && (
                          <span style={{ fontSize: '12px', color: '#8e8e8e', cursor: 'pointer', display: 'flex', alignItems: 'center' }} onClick={() => { setNewPostTypeName(""); setNewPostTypeColor("#0DDFAB"); setShowCreatePostTypeModal(true); }}>More +</span>
                        )}
                      </div>
                    )}
                    <input 
                      type="file" 
                      multiple 
                      ref={dmFileInputRef} 
                      style={{ display: 'none' }} 
                      onChange={(e) => { setDmAttachments(prev => [...prev, ...Array.from(e.target.files)]); e.target.value = ""; }}
                    />
                  </div>
                  <button
                    className={`modern-send-btn ${(dmText.trim() || dmAttachments.length > 0 || isRecording) ? 'active' : ''}`}
                    onClick={() => {
                      if (isRecording) {
                        shouldSendVoiceNote.current = true;
                        stopRecording();
                        return;
                      }
                      if (dmText.trim() || dmAttachments.length > 0) {
                        handleSendMessage({ text: dmText, postType: selectedPostType, attachments: dmAttachments });
                        setDmText("");
                        setDmAttachments([]);
                        setReplyTo(null);
                        setEditingMessage(null);
                        setShowDmEmojiPicker(false);
                      }
                    }}
                  >
                    {editingMessage ? <Check size={18} strokeWidth={3} /> : <SendHorizontal size={16} />}
                  </button>
                </div>
              </div>
              )}
            </div>
          ) : (
            <div className="channel-layout">
              {/* Header for new-channel / new-DM mode (no active room) */}
              {!activeRoom && (
                <div className="channel-header-modern">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div className="channel-title-row">
                      <div className="channel-icon-box">
                        <img src="/assets/images/chatusers.svg" alt="chat" style={{ width: 18, height: 18 }} />
                      </div>
                      <div className="channel-title-text">
                        {isNewChannelMode ? "New Channel" : "New Chat"}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                      <div onClick={handleCloseChat} style={{ border: '1px solid #f0f0f0', background: '#fff', width: '28px', height: '28px', minWidth: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', cursor: 'pointer', padding: 0 }}>
                        <ChevronLeft size={12} color="#8e8e8e" className="back-btn-icon" strokeWidth={2.5} />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {/* DM Header — only shown when a room is active */}
              {activeRoom && <div className="channel-header-modern">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div className="channel-title-row">
                    {(() => {
                      const url = getRoomDisplayAvatar(activeRoom);
                      const initials = getRoomDisplayName(activeRoom).split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();
                      return (
                        <div style={{
                          width: 30, height: 30, minWidth: 30, borderRadius: 8, overflow: "hidden",
                          background: "#0DDFAB", display: "flex", alignItems: "center", justifyContent: "center",
                          color: "#fff", fontWeight: 600, fontSize: 12, position: "relative", flexShrink: 0,
                        }}>
                          <span style={{ pointerEvents: "none", userSelect: "none" }}>{initials}</span>
                          {url && (
                            <img
                              src={url}
                              alt=""
                              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0 }}
                              onLoad={e => { e.target.style.opacity = "1"; }}
                              onError={e => { e.target.style.display = "none"; }}
                            />
                          )}
                        </div>
                      );
                    })()}
                    <div className="channel-title-text">{getRoomDisplayName(activeRoom) || "Chat"}</div>
                  </div>
                  <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                    <div onClick={handleCloseChat} style={{ border: '1px solid #f0f0f0', background: '#fff', width: '28px', height: '28px', minWidth: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', cursor: 'pointer', padding: 0 }}>
                      <ChevronLeft size={12} color="#8e8e8e" className="back-btn-icon" strokeWidth={2.5} />
                    </div>
                  </div>
                </div>
                {/* Filter chips — Date and Attachments always shown */}
                <div className="channel-filters-row">
                  <div style={{ position: "relative" }}>
                    <DatePicker.RangePicker
                      open={datePickerOpen}
                      onOpenChange={(open) => { setDatePickerOpen(open); }}
                      onChange={(dates) => {
                        setSelectedDateRange(dates);
                        if (dates) {
                          setDateRangeSelected(true);
                          const isToday = dates[0].isSame(moment(), 'day') && dates[1].isSame(moment(), 'day');
                          if (isToday) {
                            setFloatingDateLabel("Today's Chat");
                          } else {
                            setFloatingDateLabel(dates[0].format("MMM DD") + " - " + dates[1].format("MMM DD"));
                          }
                        } else {
                          setDateRangeSelected(false);
                          setFloatingDateLabel("Today's Chat");
                        }
                      }}
                      dropdownClassName="chat-date-picker-popup"
                      style={{ position: "absolute", opacity: 0, width: 0, height: 0, padding: 0, border: "none", pointerEvents: "none" }}
                      suffixIcon={null}
                      allowClear={false}
                      inputReadOnly
                      ranges={{
                        Today: [moment(), moment()],
                        "Last 7 days": [moment().subtract(7, "d"), moment()],
                        "Last 30 days": [moment().subtract(30, "d"), moment()],
                        "Last 90 days": [moment().subtract(90, "d"), moment()],
                      }}
                    />
                    <div className={`filter-chip ${datePickerOpen || dateRangeSelected ? "filter-chip-active" : ""}`} onClick={() => setDatePickerOpen(true)}>
                      {dateRangeSelected && selectedDateRange ? (
                        <>
                          <span className="filter-chip-icon" style={{ maskImage: "url(/assets/images/date.svg)", WebkitMaskImage: "url(/assets/images/date.svg)" }} /> {selectedDateRange[0].format("MMM D")} – {selectedDateRange[1].format("MMM D")}
                          <X size={12} style={{ marginLeft: 4, flexShrink: 0 }} onClick={(e) => { e.stopPropagation(); clearDateFilter(); }} />
                        </>
                      ) : (
                        <><span className="filter-chip-icon" style={{ maskImage: "url(/assets/images/date.svg)", WebkitMaskImage: "url(/assets/images/date.svg)" }} /> Date <ChevronDown size={14} /></>
                      )}
                    </div>
                  </div>
                  <div
                    className={`filter-chip ${showAttachmentsPanel ? "filter-chip-active" : ""}`}
                    onClick={() => setShowAttachmentsPanel(p => !p)}
                  >
                    <span className="filter-chip-icon" style={{ maskImage: "url(/assets/images/attachments.svg)", WebkitMaskImage: "url(/assets/images/attachments.svg)" }} /> Attachments <ChevronDown size={14} />
                  </div>
                  {pinnedMessages.length > 0 && (
                    <div
                      className={`filter-chip ${showPinnedPanel ? "filter-chip-active" : ""}`}
                      onClick={() => setShowPinnedPanel(p => !p)}
                    >
                      <Pin size={14} /> Pinned <ChevronDown size={14} />
                    </div>
                  )}

                  {/* Search field — always visible, right-aligned (DM) */}
                  <div className={`chat-msgsearch-wrap${msgSearchFocused ? " has-focus" : ""}${msgSearchQuery ? " has-value" : ""}`} style={{ marginLeft: "auto" }}>
                    <input
                      ref={msgSearchRef}
                      className="chat-msgsearch"
                      type="text"
                      value={msgSearchQuery}
                      onChange={e => setMsgSearchQuery(e.target.value)}
                      onFocus={() => setMsgSearchFocused(true)}
                      onBlur={() => setMsgSearchFocused(false)}
                    />
                    <label className="chat-msgsearch-label">Search messages...</label>
                    {msgSearchQuery && (
                      <i className="uil-times chat-msgsearch-clear" onClick={() => setMsgSearchQuery("")} />
                    )}
                  </div>
                </div>
              </div>}

              {/* DM Body + Attachments panel flex row */}
              <div style={{ flex: 1, display: "flex", overflow: "hidden", position: 'relative', zIndex: 1 }}>
                <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "visible" }}>
                  {(!activeRoom || (messages.length === 0 && !msgLoading)) ? (
                    /* No room or truly empty — vertically centered empty state */
                    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <div className="channel-welcome-banner" style={{ border: "none", marginBottom: 0 }}>
                        <img src="/assets/images/Mask group.svg" alt="workspace" style={{ width: 52, height: 52, marginBottom: 8 }} />
                        {isNewChannelMode && !activeRoom ? (
                          <>
                            <div className="channel-welcome-banner-title">Create a Channel</div>
                            <div className="channel-welcome-banner-desc">Channels keep your team's conversations organised by topic.</div>
                            <button className="welcome-add-btn" onClick={() => { setShowCreateChannelPanel(true); setNewChannelName(""); setChannelNameError(""); }}>
                              <Hash size={15} /> Create Channel
                            </button>
                          </>
                        ) : (
                          <>
                            <div className="channel-welcome-banner-title">Private workspace</div>
                            <div className="channel-welcome-banner-desc">A place just for you to capture ideas, draft messages, and keep everything organised for later.</div>
                            {!activeRoom && (
                              <button className="welcome-add-btn" onClick={() => {
                                setShowAddPeoplePanel(true);
                                setAddPeopleSearch("");
                                setSelectedForInvite([]);
                                ChatService.searchUsers("").then(r => { if (r.Good) setAddPeopleResults(r.users); }).catch(() => {});
                              }}>
                                <UserPlus size={15} /> Add People
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ) : msgLoading ? (
                    /* Loading: shimmer */
                    <div style={{ flex: 1 }}><ChatShimmer /></div>
                  ) : (
                    <>
                      <div style={{ flex: 1, overflowY: "auto", padding: "0 24px 24px", opacity: 1 }} ref={scrollRef} onScroll={handleScroll}>
                        <div style={{ position: "sticky", top: 0, zIndex: 10, background: "rgba(255,255,255,0.9)", backdropFilter: "blur(4px)", padding: "8px 0", display: "flex", alignItems: "center", margin: "4px 0", gap: "12px" }}>
                          <div style={{ flex: 1, height: "1px", background: "#ebebeb" }} />
                          <div className="today-discussion-pill">
                            {floatingDateLabel} <ChevronDown size={14} />
                          </div>
                          <div style={{ flex: 1, height: "1px", background: "#ebebeb" }} />
                        </div>

                        <div className="channel-welcome-banner" style={{ padding: '12px 0', marginBottom: 10 }}>
                          <img src="/assets/images/Mask group.svg" alt="private workspace" style={{ width: 52, height: 52, marginBottom: 8 }} />
                          <div className="channel-welcome-banner-title">Private workspace</div>
                          <div className="channel-welcome-banner-desc">A place just for you to capture ideas, draft messages, and keep everything organized for later.</div>
                        </div>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        {(() => {
                          const filtered = messages.filter(m => {
                            const matchesSearch = !chatSearchQuery.trim() || m.text?.toLowerCase().includes(chatSearchQuery.toLowerCase());
                            const matchesDate = !selectedDateRange || moment(m.createdAt).isBetween(selectedDateRange[0], selectedDateRange[1], 'day', '[]');
                            const matchesMsgSearch = !msgSearchQuery.trim() || (m.message || m.content || m.text || "").toLowerCase().includes(msgSearchQuery.toLowerCase()) || (m.senderName || m.sender_name || "").toLowerCase().includes(msgSearchQuery.toLowerCase());
                            return matchesSearch && matchesDate && matchesMsgSearch;
                          });

                          let lastDate = null;
                          return filtered.map((msg, idx) => {
                            const msgDate = moment(msg.createdAt).format("YYYY-MM-DD");
                            const showDivider = msgDate !== lastDate;
                            lastDate = msgDate;
                            const dmDateLabel = (() => {
                              const d = new Date(msg.createdAt);
                              const today = new Date();
                              if (d.toDateString() === today.toDateString()) return "Today's Chat";
                              return d.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' });
                            })();

                            const isOwn = String(msg.sender_id) === String(currentUserId);
                            const time = new Date(msg.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
                            const prevMsg = filtered[idx - 1];
                            const nextMsg = filtered[idx + 1];
                            const showTime = showDivider || !prevMsg || String(prevMsg.sender_id) !== String(msg.sender_id) || moment(prevMsg.createdAt).format("HH:mm") !== moment(msg.createdAt).format("HH:mm");
                            const nextIsGrouped = nextMsg && String(nextMsg.sender_id) === String(msg.sender_id) && moment(nextMsg.createdAt).format("HH:mm") === moment(msg.createdAt).format("HH:mm");
                            const initials = msg.sender_name?.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() || "?";
                            return (
                              <React.Fragment key={msg._stableKey || msg._id || msg.id || idx}>
                                {showDivider && (
                                  <div className="chat-date-divider" data-date-label={dmDateLabel} style={{ height: 0, overflow: 'hidden', margin: 0 }} />
                                )}
                                <div id={`msg-${String(msg._id || msg.id)}`} className={`modern-bubble-row ${isOwn ? "own" : ""}${!showTime ? " grouped" : ""}${showTime && nextIsGrouped ? " grouped-start" : ""}`}>
                                  {showTime ? (
                                    <Avatar
                                      size={26}
                                      src={msg.sender_image ? getImageUrl(msg.sender_image) : null}
                                      style={{ background: "#0DDFAB", color: "#fff", fontSize: 10, flexShrink: 0, marginTop: '4px' }}
                                    >
                                      {initials}
                                    </Avatar>
                                  ) : null}
                                  <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: isOwn ? "flex-end" : "flex-start" }}>
                                    {/* ── Meta row outside bubble: forwarded/edited · time (first in group only) ── */}
                                    {(showTime || msg.is_forwarded || msg.is_edited) && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 3, width: '100%', justifyContent: isOwn ? 'flex-end' : 'flex-start', flexWrap: 'wrap' }}>
                                      {msg.is_forwarded && (
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: 10, color: '#8696a0', fontStyle: 'italic' }}>
                                          <CornerUpRight size={9} strokeWidth={2} />Forwarded
                                        </span>
                                      )}
                                      {msg.is_edited && !msg.is_forwarded && (
                                        <span style={{ fontSize: 10, color: '#8696a0', fontStyle: 'italic' }}>Edited</span>
                                      )}
                                      {showTime && (
                                  <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                    {isOwn && (
                                      msg.isFailed ? <AlertCircle size={11} style={{ color: '#ff4d4f' }} /> :
                                      msg.isSending ? <Clock size={11} className="tick-sending" /> :
                                      isMsgRead(msg) ? <CheckCheck size={12} className="tick-read" /> :
                                      msg.isDelivered ? <CheckCheck size={12} className="tick-delivered" /> :
                                      <Check size={12} className="tick-sent" />
                                    )}
                                    <span style={{ fontSize: 10, color: '#9aa0a6' }}>{moment(msg.createdAt).format("h:mm A")}</span>
                                  </span>
                                )}
                                    </div>
                                    )}
                                    <div className="modern-bubble" style={{ opacity: msg.isSending ? 0.6 : 1 }}>
                                    {msg.reply_to && (
                                      <div className="bubble-reply-reference" onClick={() => scrollToMessage(msg.reply_to)}>
                                        {(() => {
                                          const parent = messages.find(m => (m._id || m.id) === msg.reply_to);
                                          return (
                                            <>
                                              <div className="reply-ref-name">{parent?.sender_name || "Original Message"}</div>
                                              <div className="reply-ref-text">{parent?.text || "Attachment/Voice Note"}</div>
                                            </>
                                          );
                                        })()}
                                      </div>
                                    )}
                                    {msg.is_pinned && (
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#1a73e8', marginBottom: '6px' }}>
                                        <Pin size={10} fill="#1a73e8" /> <span>Pinned</span>
                                      </div>
                                    )}
                                    <div className="modern-bubble-text">{renderMessageText(msg.text)}</div>
                                    {msg.attachments?.length > 0 && (() => {
                                      const baseUrl = (process.env.REACT_APP_BASE_URL || "http://localhost:5026/api/v1").split("/api/v1")[0];
                                      const getUrl = (f) => f.isLocal ? f.url : `${baseUrl}${f.url?.startsWith("/public") ? f.url : `/public${f.url}`}`;
                                      const imgFiles   = msg.attachments.filter(f => f.fileType === "image" || /\.(jpg|jpeg|png|gif|webp)$/i.test(f.url || ""));
                                      const otherFiles = msg.attachments.filter(f => !(f.fileType === "image" || /\.(jpg|jpeg|png|gif|webp)$/i.test(f.url || "")));
                                      const cols = imgFiles.length === 1 ? 1 : imgFiles.length === 2 ? 2 : 3;
                                      return (
                                        <>
                                          {imgFiles.length > 0 && (
                                            <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 3, marginTop: 6, maxWidth: cols === 1 ? 200 : cols === 2 ? 244 : 270, borderRadius: 8, overflow: "hidden" }}>
                                              {imgFiles.map((f, i) => (
                                                <img key={i} src={getUrl(f)} alt="" style={{ width: "100%", aspectRatio: "1/1", objectFit: "cover", display: "block", cursor: "pointer" }} onClick={() => window.open(getUrl(f), "_blank")} />
                                              ))}
                                            </div>
                                          )}
                                          {otherFiles.map((file, i) => {
                                            const fileUrl = getUrl(file);
                                            const isAudio = file.fileType === "audio" || /\.(mp3|wav|webm|m4a|ogg)$/i.test(file.url || "");
                                            if (isAudio) return (
                                              <div key={i} className="voice-note-bubble" style={{ marginTop: 8 }}>
                                                <audio controls src={fileUrl} style={{ width: '220px', height: '30px' }} />
                                              </div>
                                            );
                                            const ext = (file.name?.split('.').pop() || '').toLowerCase();
                                            const iconBg = ext === 'pdf' ? '#ff4d4f' : ['doc','docx'].includes(ext) ? '#1a73e8' : ['xls','xlsx','csv'].includes(ext) ? '#0f9d58' : ['ppt','pptx'].includes(ext) ? '#ff6d00' : ['zip','rar','7z'].includes(ext) ? '#9c27b0' : '#5f6368';
                                            return (
                                              <a key={i} href={fileUrl} onClick={async (e) => { e.preventDefault(); try { const res = await fetch(fileUrl); const blob = await res.blob(); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = file.name || 'file'; a.click(); URL.revokeObjectURL(url); } catch { window.open(fileUrl, '_blank'); } }} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: 8, padding: '8px 12px', background: 'rgba(0,0,0,0.04)', borderRadius: '10px', textDecoration: 'none', border: '1px solid rgba(0,0,0,0.08)', maxWidth: '240px', cursor: 'pointer' }}>
                                                <div style={{ width: 36, height: 36, borderRadius: '8px', background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                  <FileText size={18} color="#fff" />
                                                </div>
                                                <div style={{ flex: 1, overflow: 'hidden' }}>
                                                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#1d1d1d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name || "File"}</div>
                                                  <div style={{ fontSize: '11px', color: '#5f6368', marginTop: '2px' }}>{ext ? ext.toUpperCase() : 'FILE'}</div>
                                                </div>
                                                <i className="uil-import" style={{ fontSize: '16px', color: '#5f6368', flexShrink: 0 }} />
                                              </a>
                                            );
                                          })}
                                        </>
                                      );
                                    })()}
                                    {msg.reactions?.length > 0 && (
                                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px' }}>
                                        {msg.reactions.map((r, i) => (
                                          <Tooltip key={i} title={getReactionTooltip(r.users)} placement="top">
                                            <div className="reaction-badge" onClick={() => handleToggleReaction(msg._id, r.emoji)} style={{
                                              background: r.users.includes(currentUserId) ? '#e0faf3' : '#f0f0f0',
                                              border: r.users.includes(currentUserId) ? '1px solid #0DDFAB' : '1px solid #e0e0e0',
                                              borderRadius: '12px',
                                              padding: '2px 6px',
                                              fontSize: '11px',
                                              cursor: 'pointer',
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: '4px'
                                            }}>
                                              <span>{r.emoji}</span>
                                              <span style={{ color: '#5f6368' }}>{r.users.length}</span>
                                            </div>
                                          </Tooltip>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                  {!(msg.isSending || msg.isFailed) && myPermission !== "View Only" && <div className={`modern-bubble-actions${openReactionPopoverId === msg._id ? ' reaction-open' : ''}`} style={isOwn ? { marginRight: 44 } : { marginLeft: 44 }}>
                                    <span className="action-tip" data-tip="Like"><FaThumbsUp size={12} className="modern-action-icon" onClick={() => handleToggleReaction(msg._id, '👍')} /></span>
                                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                      <span className="action-tip" data-tip="Add Reaction"><FaSmile size={12} className="modern-action-icon" onClick={(e) => { const rect = e.currentTarget.getBoundingClientRect(); setReactionPickerDir({ v: window.innerHeight - rect.bottom < 390 ? 'up' : 'down', h: rect.left + rect.width / 2 < window.innerWidth / 2 ? 'left' : 'right' }); setOpenReactionPopoverId(openReactionPopoverId === msg._id ? null : msg._id); }} /></span>
                                      {openReactionPopoverId === msg._id && (
                                        <div className="reaction-picker-wrapper" style={{ position: 'absolute', ...(reactionPickerDir.v === 'up' ? { bottom: '30px' } : { top: '30px' }), ...(reactionPickerDir.h === 'right' ? { right: 0 } : { left: 0 }), zIndex: 100, background: '#fff', borderRadius: '12px', boxShadow: '0 8px 30px rgba(0,0,0,0.15)', padding: '12px' }} onClick={e => e.stopPropagation()}>
                                          <div className="float-input-wrap float-input-icon" style={{ marginBottom: '4px' }}>
                                            <i className="uil-search" style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', color: '#aaa', fontSize: '15px', pointerEvents: 'none', zIndex: 2 }} />
                                            <input type="text" placeholder=" " className="form-control-custom-focus" style={{ padding: '0 38px 0 14px', borderRadius: '8px', height: '38px', border: '1.5px solid #d0d0d0', lineHeight: '38px', width: '100%' }} onChange={e => { const val = e.target.value; const root = e.currentTarget.closest('.reaction-picker-wrapper'); const inp = root?.querySelector('input:not(.form-control-custom-focus)'); if (inp) { const s = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set; s.call(inp, val); inp.dispatchEvent(new Event('input', { bubbles: true })); inp.dispatchEvent(new Event('change', { bubbles: true })); } }} />
                                            <label style={{ fontSize: '13px', left: '14px' }}>Search emojis...</label>
                                          </div>
                                          <EmojiPicker
                                            onEmojiClick={(emojiData) => { handleToggleReaction(msg._id, emojiData.emoji); setOpenReactionPopoverId(null); }}
                                            skinTonesDisabled
                                            previewConfig={{ showPreview: false }}
                                            width={350}
                                            height={300}
                                          />
                                        </div>
                                      )}
                                    </div>
                                    <span className="action-tip" data-tip={msg.is_pinned ? "Unpin" : "Pin"}><FaThumbtack size={12} className="modern-action-icon" onClick={() => handleTogglePin(msg._id)} style={{ color: msg.is_pinned ? '#1a73e8' : '' }} /></span>
                                    <span className="action-tip" data-tip="Reply"><FaReply size={12} className="modern-action-icon" onClick={() => setReplyTo(msg._id || msg.id)} /></span>
                                    <span className="action-tip" data-tip="Forward"><FaShare size={12} className="modern-action-icon" onClick={() => { setForwardMsg(msg); setShowForwardModal(true); }} /></span>
                                    {isOwn && !isVoiceNote(msg) && isWithinOneHour(msg) && <span className="action-tip" data-tip="Edit"><FaPen size={12} className="modern-action-icon" onClick={() => { setEditingMessage(msg); setEditingAttachments(msg.attachments || []); setDmText(msg.text || ""); }} /></span>}
                                    {isOwn && isWithinOneHour(msg) && <span className="action-tip" data-tip="Delete"><FaTrash size={12} className="modern-action-icon" onClick={() => { setMessageToDelete(msg._id || msg.id); setDeleteFor("everyone"); document.getElementById("openChatMsgDeleteModal").click(); }} style={{ color: '#ff4d4f' }} /></span>}
                                  </div>}
                                </div>
                              </div>
                                </React.Fragment>
                            );
                          });
                        })()}
                      </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Add People panel — for new DM mode (no activeRoom) */}
                {showAddPeoplePanel && !activeRoom && (
                  <div className="add-people-panel">
                    <div className="add-people-panel-header">
                      <span>Add People</span>
                      <div style={{ cursor: "pointer" }} onClick={() => setShowAddPeoplePanel(false)}>
                        <X size={16} color="#5f6368" />
                      </div>
                    </div>
                    <div className="add-people-search-row">
                      <div className="float-input-wrap float-input-icon" style={{ flex: 1, margin: 0 }}>
                        <i className="uil-search" style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", color: "#aaa", fontSize: "15px", pointerEvents: "none", zIndex: 2 }} />
                        <input
                          type="search"
                          autoComplete="off"
                          data-form-type="other"
                          placeholder=" "
                          value={addPeopleSearch}
                          onChange={e => handleAddPeopleSearch(e.target.value)}
                          autoFocus
                          style={{ padding: "0 36px 0 14px", borderRadius: "5px", height: "36px", border: "1.5px solid #d0d0d0", lineHeight: "36px", margin: 0, width: "100%" }}
                          className="form-control-custom-focus"
                        />
                        <label style={{ fontSize: "13px", left: "14px" }}>Search people</label>
                      </div>
                    </div>
                    <div className="add-people-list">
                      {(() => {
                        // Users who already have an active DM should not appear in search
                        const activeDmUserIds = new Set(
                          rooms
                            .filter(r => r.type === "direct")
                            .flatMap(r => (r.members || []).map(m => String(m.id || m._id)))
                            .filter(id => id !== String(currentUserId))
                        );
                        return addPeopleResults
                          .filter(u => !activeDmUserIds.has(String(u.id)))
                          .map(u => (
                            <div
                              key={u.id}
                              className="add-people-item"
                              onClick={() => { handleStartDirectChat(u.id); setShowAddPeoplePanel(false); }}
                            >
                              <Avatar size={30} src={getImageUrl(u.image)} style={{ background: "#0DDFAB", fontSize: 12, flexShrink: 0 }}>
                                {u.first_name?.charAt(0)}
                              </Avatar>
                              <span className="add-people-name">{u.first_name} {u.last_name}</span>
                            </div>
                          ));
                      })()}
                    </div>
                  </div>
                )}
                {/* Create Channel panel — for new channel mode (no activeRoom) */}
                {showCreateChannelPanel && !activeRoom && (
                  <div className="add-people-panel">
                    <div className="add-people-panel-header">
                      <span>Create Channel</span>
                      <div style={{ cursor: "pointer" }} onClick={() => setShowCreateChannelPanel(false)}>
                        <X size={16} color="#5f6368" />
                      </div>
                    </div>
                    <div className="add-people-search-row">
                      <div className="float-input-wrap float-input-icon" style={{ flex: 1, margin: 0 }}>
                        <input
                          type="text"
                          autoComplete="new-password"
                          data-form-type="other"
                          placeholder=" "
                          value={newChannelName}
                          maxLength={30}
                          autoFocus
                          onChange={e => { setNewChannelName(e.target.value); if (e.target.value.trim()) setChannelNameError(""); }}
                          onKeyDown={e => { if (e.key === "Enter") handleCreateNewChannel(); if (e.key === "Escape") setShowCreateChannelPanel(false); }}
                          style={{ padding: "0 14px", borderRadius: "5px", height: "36px", border: `1.5px solid ${channelNameError ? "#ff4d4f" : "#d0d0d0"}`, lineHeight: "36px", margin: 0, width: "100%" }}
                          className="form-control-custom-focus"
                        />
                        <label style={{ fontSize: "13px", left: "14px" }}>Channel name</label>
                      </div>
                    </div>
                    {channelNameError && <small style={{ color: "#ff4d4f", fontSize: "11px", padding: "0 12px" }}>{channelNameError}</small>}
                    <div style={{ padding: "10px 12px 12px" }}>
                      <button
                        className="welcome-add-btn"
                        style={{ width: "100%", justifyContent: "center", opacity: isCreatingChannel ? 0.6 : 1 }}
                        disabled={isCreatingChannel}
                        onClick={handleCreateNewChannel}
                      >
                        {isCreatingChannel ? "Creating..." : "Create Channel"}
                      </button>
                    </div>
                  </div>
                )}
                {/* Attachments panel — flex sibling, same as channel */}
                {showAttachmentsPanel && (() => {
                  const baseUrl = (process.env.REACT_APP_BASE_URL || "http://localhost:5026/api/v1").split("/api/v1")[0];
                  const allAttachments = messages.flatMap(m => (m.attachments || []).map(a => ({ ...a, sender_name: m.sender_name, createdAt: m.createdAt })));
                  const images = allAttachments.filter(a => (a.fileType === "image" || /\.(jpg|jpeg|png|gif|webp)$/i.test(a.url)) && !(a.fileType === "audio" || /\.(mp3|wav|m4a|ogg)$/i.test(a.url)));
                  const videos = allAttachments.filter(a => (a.fileType === "video" || /\.(mp4|mov|avi)$/i.test(a.url)) && !(a.fileType === "audio" || /\.(mp3|wav|m4a|ogg|webm)$/i.test(a.url)));
                  const docs   = allAttachments.filter(a => !images.includes(a) && !videos.includes(a) && !(a.fileType === "audio" || /\.(mp3|wav|m4a|ogg|webm)$/i.test(a.url)) && !/^https?:\/\//i.test(a.url));
                  const links  = messages.flatMap(m => {
                    const matches = (m.text || "").match(/https?:\/\/[^\s]+/g) || [];
                    return matches.map(url => ({ url, sender_name: m.sender_name, createdAt: m.createdAt }));
                  });
                  const current = ({ Images: images, Videos: videos, Docs: docs, Links: links })[attachmentsTab] || [];
                  const getUrl = (a) => { const norm = a.url?.startsWith("/public") ? a.url : `/public${a.url}`; return `${baseUrl}${norm}`; };
                  return (
                    <div className="attachments-panel">
                      <div className="attachments-tabs">
                        {[
                          { label: "Images", icon: <Image size={12} /> },
                          { label: "Videos", icon: <Video size={12} /> },
                          { label: "Docs",   icon: <FileText size={12} /> },
                          { label: "Links",  icon: <Link2 size={12} /> },
                        ].map(({ label, icon }) => (
                          <div key={label} className={`attachments-tab ${attachmentsTab === label ? "active" : ""}`} onClick={() => setAttachmentsTab(label)}>
                            {icon} {label}
                          </div>
                        ))}
                      </div>
                      <div className="attachments-content">
                        {current.length === 0 ? (
                          <div style={{ textAlign: "center", color: "#9e9e9e", fontSize: 12, marginTop: 40 }}>No {attachmentsTab.toLowerCase()} yet</div>
                        ) : attachmentsTab === "Images" ? (
                          <div className="attachments-grid">{current.map((a, i) => <img key={i} src={getUrl(a)} alt="" className="attachment-thumb" onClick={() => window.open(getUrl(a), "_blank")} />)}</div>
                        ) : attachmentsTab === "Videos" ? (
                          <div className="attachments-grid">{current.map((a, i) => <video key={i} src={getUrl(a)} className="attachment-thumb" controls />)}</div>
                        ) : attachmentsTab === "Links" ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{current.map((l, i) => <a key={i} href={l.url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: "#1a73e8", wordBreak: "break-all", padding: "6px 0", borderBottom: "1px solid #f0f0f0" }}>{l.url}</a>)}</div>
                        ) : (
                          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{current.map((a, i) => <a key={i} href={getUrl(a)} target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px", borderRadius: 8, background: "#f8f9fa", fontSize: 12, color: "#1d1d1d" }}><Paperclip size={14} />{a.name || "Document"}</a>)}</div>
                        )}
                      </div>
                    </div>
                  );
                })()}
              {/* DM Pinned Messages panel */}
              {showPinnedPanel && (
                <div className="attachments-panel">
                  <div style={{ padding: '12px 16px', fontWeight: 600, fontSize: 13, borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Pin size={14} /> Pinned Messages</span>
                    <X size={14} style={{ cursor: 'pointer', color: '#9e9e9e' }} onClick={() => setShowPinnedPanel(false)} />
                  </div>
                  <div className="attachments-content" style={{ padding: '8px 0' }}>
                    {pinnedMessages.length === 0 ? (
                      <div style={{ textAlign: 'center', color: '#9e9e9e', fontSize: 12, marginTop: 40 }}>No pinned messages</div>
                    ) : pinnedMessages.map((msg, i) => (
                      <div key={msg._id || i} style={{ padding: '10px 16px', borderBottom: '1px solid #f8f8f8' }}>
                        <div style={{ fontWeight: 600, fontSize: 11, color: '#1a73e8', marginBottom: 3 }}>{msg.sender_name}</div>
                        <div style={{ fontSize: 12, color: '#3c4043', lineHeight: 1.5, wordBreak: 'break-word' }}>{msg.text || "📎 Attachment"}</div>
                        <div style={{ fontSize: 10, color: '#9e9e9e', marginTop: 4 }}>{moment(msg.createdAt).format("MMM D, h:mm a")}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              </div>

              <div className="post-topic-container dm-private-footer" style={{ position: 'relative', overflow: 'visible', zIndex: 100 }}>
                {/* Attachment Previews */}
                {!editingMessage && dmAttachments.length > 0 && (
                  <div className="d-flex flex-wrap" style={{ gap: '8px', padding: '10px 20px', borderBottom: '1px solid #eee' }}>
                    {dmAttachments.map((file, i) => (
                      <div key={i} title={file.name} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#1D1D1D', borderRadius: '4px', padding: '0 8px', height: '24px', fontSize: '11px', color: '#0DDFAB', maxWidth: '200px', fontWeight: 500 }}>
                        <i className="uil-import" style={{ fontSize: '11px', flexShrink: 0 }} />
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{file.name}</span>
                        <span onClick={() => setDmAttachments(dmAttachments.filter((_, idx) => idx !== i))} style={{ flexShrink: 0, cursor: 'pointer', fontSize: '13px', lineHeight: 1, marginLeft: '2px' }}>×</span>
                      </div>
                    ))}
                  </div>
                )}
                {replyTo && (
                  <div className="reply-preview">
                    <div className="reply-preview-content">
                      <span className="reply-label">Replying to:</span>
                      <span className="reply-text">{getAttachmentPreview(messages.find(m => (m._id || m.id) === (replyTo?._id || replyTo?.id || replyTo))) || "Message"}</span>
                    </div>
                    <X size={14} className="close-reply" onClick={() => setReplyTo(null)} />
                  </div>
                )}
                {editingMessage && (
                  <>
                    <div className="edit-preview">
                      <span className="edit-label">Editing Message</span>
                      <X size={14} className="close-edit" onClick={() => { setEditingMessage(null); setEditingAttachments([]); setDmAttachments([]); setDmText(""); }} />
                    </div>
                    {renderEditAttachmentPills()}
                  </>
                )}
                <textarea
                  className="dm-footer-input"
                  ref={dmTextareaRef}
                  rows={1}
                  placeholder={activeRoom ? "Type anything..." : isNewChannelMode ? "Create a channel to start chatting..." : "Select someone to start chatting..."}
                  disabled={!activeRoom}
                  value={dmText}
                  onChange={e => { if (!activeRoom) return; setDmText(e.target.value); handleTyping(true); const t = e.target; t.style.height = '24px'; t.style.height = Math.min(t.scrollHeight, 100) + 'px'; }}
                  onBlur={() => handleTyping(false)}
                  onPaste={handleDmPaste}
                  onKeyDown={e => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      if (isRecording) {
                        shouldSendVoiceNote.current = true;
                        stopRecording();
                        return;
                      }
                      if (dmText.trim() || dmAttachments.length > 0) {
                        handleSendMessage({ text: dmText, attachments: dmAttachments });
                        setDmText("");
                        setDmAttachments([]);
                        setShowDmEmojiPicker(false);
                        if (dmTextareaRef.current) { dmTextareaRef.current.style.height = '24px'; }
                      }
                    }
                  }}
                />
                <div className="input-toolbar-modern" style={!activeRoom ? { opacity: 0.4, pointerEvents: 'none' } : {}}>
                  <div className="toolbar-left-icons">
                    {!isRecording ? (
                      <>
                        <div className="toolbar-icon-box" onClick={() => dmFileInputRef.current.click()}><Plus size={16} strokeWidth={2.5} /></div>
                        <div className="toolbar-icon-box" style={{ position: 'relative' }} onClick={() => setShowDmEmojiPicker(!showDmEmojiPicker)}>
                          <span className="emoji-mask-icon" style={{ maskImage: "url(/assets/images/emojie.svg)", WebkitMaskImage: "url(/assets/images/emojie.svg)", backgroundColor: showDmEmojiPicker ? "#0DDFAB" : "currentColor" }} />
                          {showDmEmojiPicker && (
                            <>
                              <div style={{ position: 'fixed', inset: 0, zIndex: 999, cursor: 'default' }} onClick={() => setShowDmEmojiPicker(false)} />
                              <div style={{ position: 'absolute', bottom: '40px', left: 0, zIndex: 1000, background: '#fff', borderRadius: '12px', boxShadow: '0 8px 30px rgba(0,0,0,0.15)', padding: '12px' }} onClick={e => e.stopPropagation()}>
                                <div className="float-input-wrap float-input-icon" style={{ marginBottom: "12px" }}>
                                  <i className="uil-search" style={{ position: "absolute", right: "14px", top: "50%", transform: "translateY(-50%)", color: "#aaa", fontSize: "15px", pointerEvents: "none", zIndex: 2 }} />
                                  <input
                                    type="text"
                                    autoComplete="new-password"
                                    data-form-type="other"
                                    placeholder=" "
                                    className="form-control-custom-focus"
                                    style={{ padding: "0 38px 0 14px", borderRadius: "8px", height: "38px", border: "1.5px solid #d0d0d0", lineHeight: "38px", width: "100%" }}
                                    onChange={e => {
                                      const val = e.target.value;
                                      const pickerRoot = e.currentTarget.closest('div[style*="z-index: 1000"]');
                                      const internalInput = pickerRoot?.querySelector('input:not(.form-control-custom-focus)');
                                      if (internalInput) {
                                        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value").set;
                                        nativeInputValueSetter.call(internalInput, val);
                                        internalInput.dispatchEvent(new Event('input', { bubbles: true }));
                                        internalInput.dispatchEvent(new Event('change', { bubbles: true }));
                                      }
                                    }}
                                  />
                                  <label style={{ fontSize: "13px", left: "14px" }}>Search emojis...</label>
                                </div>
                                <EmojiPicker onEmojiClick={(emoji) => setDmText(prev => prev + emoji.emoji)} width={350} height={400} />
                              </div>
                            </>
                          )}
                        </div>
                        <div className="toolbar-icon-box" onClick={startRecording}><span className="audio-mask-icon" style={{ maskImage: "url(/assets/images/audio.svg)", WebkitMaskImage: "url(/assets/images/audio.svg)" }} /></div>
                      </>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#ff4d4f', fontWeight: '600', fontSize: '13px' }}>
                        <div className={`recording-dot ${isPaused ? 'paused' : ''}`} /> Recording {formatTime(recordingTime)}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '8px' }}>
                          {isPaused ? (
                            <Play size={14} style={{ cursor: 'pointer', color: '#0DDFAB' }} onClick={resumeRecording} />
                          ) : (
                            <Pause size={14} style={{ cursor: 'pointer', color: '#1a73e8' }} onClick={pauseRecording} />
                          )}
                          <span style={{ color: '#5f6368', cursor: 'pointer', fontSize: '12px' }} onClick={() => { setIsRecording(false); setIsPaused(false); clearInterval(recordingIntervalRef.current); mediaRecorderRef.current.onstop = null; mediaRecorderRef.current.stop(); mediaStreamRef.current?.getTracks().forEach(t => t.stop()); }}>Cancel</span>
                        </div>
                      </div>
                    )}
                    <input 
                      type="file" 
                      multiple 
                      ref={dmFileInputRef} 
                      style={{ display: 'none' }} 
                      onChange={(e) => { setDmAttachments(prev => [...prev, ...Array.from(e.target.files)]); e.target.value = ""; }}
                    />
                  </div>
                  <button
                    className={`modern-send-btn ${activeRoom && (dmText.trim() || dmAttachments.length > 0 || isRecording) ? 'active' : ''}`}
                    disabled={!activeRoom}
                    onClick={() => {
                      if (!activeRoom) return;
                      if (isRecording) {
                        shouldSendVoiceNote.current = true;
                        stopRecording();
                        return;
                      }
                      if (dmText.trim() || dmAttachments.length > 0) {
                        handleSendMessage({ text: dmText, attachments: dmAttachments });
                        setDmText("");
                        setDmAttachments([]);
                        setReplyTo(null);
                        setEditingMessage(null);
                        setShowDmEmojiPicker(false);
                        if (dmTextareaRef.current) { dmTextareaRef.current.style.height = '24px'; }
                      }
                    }}
                  >
                    {editingMessage ? <Check size={18} strokeWidth={3} /> : <SendHorizontal size={16} />}
                  </button>
                </div>
              </div>
            </div>
          )}
          </div>
        </div>
      </div>

      {/* New Chat / Channel Modal */}
      <Modal
        title={modalType === "channel" ? "Create New Channel" : "Start New Chat"}
        visible={isNewChatModal}
        onCancel={() => setIsNewChatModal(false)}
        footer={null}
        width={500}
        style={{ zIndex: 1070 }}
        mask={false}
      >
        {modalType === "dm" ? (
          <div style={{ padding: "10px 0" }}>
                        <div className="float-input-wrap float-input-icon" style={{ marginBottom: "0px" }}>
              <i className="uil-search" style={{ position: "absolute", right: "14px", top: "50%", transform: "translateY(-50%)", color: "#aaa", fontSize: "15px", pointerEvents: "none", zIndex: 2 }} />
              <input
                type="text"
                autoComplete="new-password"
                data-form-type="other"
                placeholder=" "
                value={searchQuery}
                className="form-control-custom-focus"
                style={{ padding: "0 38px 0 14px", borderRadius: "8px", height: "38px", border: "1.5px solid #d0d0d0", lineHeight: "38px", width: "100%" }}
                onChange={e => handleSearchUsers(e.target.value)}
                autoFocus
              />
              <label style={{ fontSize: "13px", left: "14px" }}>Search users...</label>
            </div>

            <div style={{ marginTop: "20px", maxHeight: "300px", overflowY: "auto" }}>
              {searchResults.map(u => (
                <div key={u.id} className="chat-room-item" style={{ borderRadius: "8px", marginBottom: "5px" }} onClick={() => handleStartDirectChat(u.id)}>
                  <Avatar src={getImageUrl(u.image)}>{u.first_name?.charAt(0)}</Avatar>
                  <div style={{ marginLeft: "10px" }}>
                    <div style={{ fontWeight: "500" }}>{u.first_name} {u.last_name}</div>
                    <div style={{ fontSize: "11px", color: "#70757a" }}>{u.email}</div>
                  </div>
                </div>
              ))}
              {searchQuery.length > 1 && searchResults.length === 0 && <div style={{ textAlign: "center", padding: "20px", color: "#70757a" }}>No users found</div>}
            </div>
            <div style={{ marginTop: "16px", borderTop: "1px solid #f0f0f0", paddingTop: "16px" }}>
              <button onClick={() => setModalType("channel")} style={{ background: "none", border: "1px solid #d9d9d9", borderRadius: "6px", padding: "6px 14px", cursor: "pointer", fontSize: "13px", color: "#555" }}>
                Create a channel instead
              </button>
            </div>
          </div>
        ) : (
          <div style={{ padding: "10px 0" }}>
            <label style={{ display: "block", marginBottom: "8px", color: "#5f6368", fontSize: "12px", fontWeight: "600" }}>CHANNEL NAME</label>
            <Input placeholder="e.g. general-discussions" value={newChannelName} onChange={e => setNewChannelName(e.target.value)} maxLength={30} autoFocus />
            <Button type="primary" block style={{ height: "40px", borderRadius: "8px", marginTop: "16px" }} disabled={!newChannelName.trim()} onClick={handleCreateChannel}>
              Create Channel
            </Button>
          </div>
        )}
      </Modal>

      {/* Add Member Modal */}
      <Modal
        title={`Add Member to ${activeRoom ? getRoomDisplayName(activeRoom) : ""}`}
        visible={isAddMemberModal}
        onCancel={() => { setIsAddMemberModal(false); setSearchQuery(""); setSearchResults([]); }}
        footer={null}
        width={500}
        style={{ zIndex: 1070 }}
        mask={false}
      >
        <div style={{ padding: "10px 0" }}>
          <div className="float-input-wrap float-input-icon" style={{ marginBottom: "0px" }}>
            <i className="uil-search" style={{ position: "absolute", right: "14px", top: "50%", transform: "translateY(-50%)", color: "#aaa", fontSize: "15px", pointerEvents: "none", zIndex: 2 }} />
            <input
              type="text"
              autoComplete="new-password"
              data-form-type="other"
              placeholder=" "
              value={searchQuery}
              className="form-control-custom-focus"
              style={{ padding: "0 38px 0 14px", borderRadius: "8px", height: "38px", border: "1.5px solid #d0d0d0", lineHeight: "38px", width: "100%" }}
              onChange={e => handleSearchUsers(e.target.value)}
              autoFocus
            />
            <label style={{ fontSize: "13px", left: "14px" }}>Search for a user to add...</label>
          </div>
          <div style={{ marginTop: "20px", maxHeight: "300px", overflowY: "auto" }}>
            {searchResults.map(u => {
              const isMember = activeRoom?.members?.some(m => String(m.id || m._id) === String(u.id));
              return (
                <div key={u.id} className={`chat-room-item ${isMember ? "disabled" : ""}`} style={{ borderRadius: "8px", marginBottom: "5px", opacity: isMember ? 0.6 : 1, cursor: isMember ? "default" : "pointer" }} onClick={() => !isMember && handleConfirmAddMember(u.id)}>
                  <Avatar src={getImageUrl(u.image)}>{u.first_name?.charAt(0)}</Avatar>
                  <div style={{ marginLeft: "10px" }}>
                    <div style={{ fontWeight: "500" }}>{u.first_name} {u.last_name}</div>
                    <div style={{ fontSize: "11px", color: "#70757a" }}>{u.email}</div>
                  </div>
                  <div style={{ marginLeft: "auto", color: isMember ? "#5f6368" : "#1a73e8", fontSize: "12px" }}>
                    {isMember ? "Already a member" : "Click to Add"}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Modal>

      {/* Room Info Modal */}
      {activeRoom && (
        <Modal 
          title="Chat Information" 
          visible={isInfoModalVisible} 
          onCancel={() => setIsInfoModalVisible(false)} 
          footer={null} 
          width={400} 
          style={{ zIndex: 1070 }}
          mask={false}
        >
          <div style={{ textAlign: "center", marginBottom: "20px" }}>
            <Avatar size={64} src={getRoomDisplayAvatar(activeRoom)}>{getRoomDisplayName(activeRoom).charAt(0)}</Avatar>
            <h3 style={{ marginTop: "10px", marginBottom: "4px" }}>{getRoomDisplayName(activeRoom)}</h3>
            <div style={{ color: "#5f6368", fontSize: "12px", textTransform: "capitalize" }}>{activeRoom.type} Chat</div>
          </div>
          <div style={{ borderTop: "1px solid #eee", paddingTop: "15px" }}>
            <div style={{ fontWeight: "600", marginBottom: "10px", fontSize: "13px" }}>Members ({activeRoom.members?.length || 0})</div>
            <div style={{ maxHeight: "300px", overflowY: "auto" }}>
              {activeRoom.members?.map(m => (
                <div key={m.id} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px 0" }}>
                  <Avatar src={getImageUrl(m.image)}>{m.first_name?.charAt(0)}</Avatar>
                  <div>
                    <div style={{ fontSize: "13px", fontWeight: "500" }}>{m.first_name} {m.last_name} {String(m.id) === String(currentUserId) && "(You)"}</div>
                    <div style={{ fontSize: "11px", color: "#70757a" }}>{m.email}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Modal>
      )}

      {/* Share this List Modal */}
      {showShareModal && (
        <div
          style={{ position: "fixed", inset: 0, zIndex: 100010, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.5)" }}
          onClick={() => { if (openPermDropdown) { setOpenPermDropdown(null); } else { closeShareModal(); } }}
        >
          <div
            style={{ background: "#fff", borderRadius: "16px", width: 490, maxWidth: "95vw", padding: "24px 24px 20px", boxShadow: "0 8px 40px rgba(0,0,0,0.18)", position: "relative" }}
            onClick={e => { e.stopPropagation(); if (openPermDropdown) setOpenPermDropdown(null); }}
          >
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "18px" }}>
              <h3 style={{ margin: 0, fontSize: "17px", fontWeight: "600", color: "#1d1d1d" }}>Invite to Channel</h3>
              <X size={18} color="#5f6368" style={{ cursor: "pointer" }} onClick={closeShareModal} />
            </div>

            {/* Email + Access (inside field) + Invite */}
            <div style={{ display: "flex", gap: "8px", marginBottom: "14px", alignItems: "center" }}>
              <div className={`share-email-wrap ${shareEmail ? "has-value" : ""}`}>
                <div className="share-email-field">
                  <input
                    type="text"
                    autoComplete="new-password"
                    data-form-type="other"
                    placeholder=" "
                    value={shareEmail}
                    onChange={e => setShareEmail(e.target.value)}
                  />
                  <button className="access-btn" onClick={e => {
                    e.stopPropagation();
                    const rect = e.currentTarget.getBoundingClientRect();
                    const dropdownHeight = 260;
                    const spaceBelow = window.innerHeight - rect.bottom;
                    const top = spaceBelow < dropdownHeight ? rect.top - dropdownHeight - 4 : rect.bottom + 4;
                    setPermDropdownPos({ top, right: window.innerWidth - rect.right });
                    setOpenPermDropdown(openPermDropdown === "__invite__" ? null : "__invite__");
                  }}>
                    {inviteAccess} <ChevronDown size={12} />
                  </button>
                </div>
                <label>Email Address</label>
              </div>
              <button
                style={{ height: "40px", padding: "0 20px", border: "none", borderRadius: "8px", background: isInviting ? "#aaa" : "#0DDFAB", color: "#fff", fontSize: "13px", fontWeight: "600", cursor: isInviting ? "not-allowed" : "pointer", whiteSpace: "nowrap", flexShrink: 0, minWidth: "80px" }}
                onClick={handleSendInvite}
                disabled={isInviting}
              >
                {isInviting ? "Inviting..." : "Invite"}
              </button>
            </div>

            {/* Share link row */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#f5f5f5", borderRadius: "8px", marginBottom: "18px", padding: "3px 3px 3px 14px", height: "38px" }}>
              <span style={{ fontSize: "13px", color: "#5f6368", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, marginRight: "8px" }}>
                {inviteLink ? inviteLink : "Click to generate an invite link"}
              </span>
              <button
                style={{ height: "100%", padding: "0 14px", background: "#fff", border: "none", borderRadius: "6px", fontSize: "12px", fontWeight: "500", color: "#1d1d1d", cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap" }}
                onClick={async () => {
                  let link = inviteLink;
                  if (!link && activeRoom) {
                    try {
                      const res = await ChatService.generateInviteLink({ roomId: activeRoom._id, permission: inviteAccess });
                      if (res.Good) { link = buildInviteLink(res.inviteLink); setInviteLink(link); }
                    } catch (err) { message.error("Could not generate invite link"); return; }
                  }
                  if (!link) return;
                  // Clipboard write using execCommand fallback (safe after async)
                  try {
                    await navigator.clipboard.writeText(link);
                  } catch {
                    const ta = document.createElement("textarea");
                    ta.value = link;
                    ta.style.position = "fixed";
                    ta.style.opacity = "0";
                    document.body.appendChild(ta);
                    ta.select();
                    document.execCommand("copy");
                    document.body.removeChild(ta);
                  }
                  message.success("Invite link copied!");
                }}
              >
                {inviteLink ? "Copy" : "Generate Link"}
              </button>
            </div>

            {/* Who has access */}
            <div style={{ fontSize: "13px", fontWeight: "600", color: "#1d1d1d", marginBottom: "8px" }}>Who has access</div>
            <div style={{ display: "flex", flexDirection: "column", maxHeight: "260px", overflowY: "auto" }}>
              {(activeRoom?.members || []).map(m => {
                const perm = memberPermissions[m.id || m._id] || "Full edit";
                const isOpen = openPermDropdown === (m.id || m._id);
                const isCreator = String(m.id || m._id) === String(activeRoom?.created_by);
                const permOptions = [
                  { label: "Full edit", desc: "Can add people, create post types, send messages, and manage the channel." },
                  { label: "Edit", desc: "Can create post types and send messages. Can't add people to the channel." },
                  { label: "Comment", desc: "Can send messages using existing post types. Can't create new post types or add people." },
                  { label: "View Only", desc: "Read only. Can view messages but cannot send or interact." },
                ];
                return (
                  <div key={m.id || m._id} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "7px 0", position: "relative", borderBottom: "1px solid #f5f5f5" }}>
                    <div style={{ width: 30, height: 30, borderRadius: "6px", background: "#0DDFAB", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                      {m.first_name?.charAt(0).toUpperCase()}
                    </div>
                    <span style={{ flex: 1, fontSize: "13px", fontWeight: "500", color: "#1d1d1d" }}>{m.first_name} {m.last_name}</span>
                    <div>
                      {isCreator ? (
                        <span style={{ fontSize: "12px", color: "#8e8e8e", fontWeight: 500, padding: "4px 8px" }}>Owner</span>
                      ) : (
                        <button
                          className="perm-btn"
                          onClick={e => {
                            e.stopPropagation();
                            const rect = e.currentTarget.getBoundingClientRect();
                            const dropdownHeight = 260;
                            const spaceBelow = window.innerHeight - rect.bottom;
                            const top = spaceBelow < dropdownHeight ? rect.top - dropdownHeight - 4 : rect.bottom + 4;
                            setPermDropdownPos({ top, right: window.innerWidth - rect.right });
                            setOpenPermDropdown(isOpen ? null : (m.id || m._id));
                          }}
                        >
                          {perm} <ChevronDown size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Create Post Type Modal */}
      {showCreatePostTypeModal && (
        <div
          style={{ position: "fixed", inset: 0, zIndex: 11000, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.35)" }}
          onClick={() => setShowCreatePostTypeModal(false)}
        >
          <div
            style={{ background: "#fff", borderRadius: "16px", width: 420, maxWidth: "95vw", padding: "24px", boxShadow: "0 8px 40px rgba(0,0,0,0.18)" }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h3 style={{ margin: 0, fontSize: "17px", fontWeight: "600", color: "#1d1d1d" }}>Create Post Type</h3>
              <X size={18} color="#5f6368" style={{ cursor: "pointer" }} onClick={() => setShowCreatePostTypeModal(false)} />
            </div>

            <div className="float-input-wrap" style={{ marginBottom: "16px" }}>
              <input
                type="text"
                autoComplete="new-password"
                data-form-type="other"
                placeholder=" "
                value={newPostTypeName}
                onChange={e => setNewPostTypeName(e.target.value)}
                onKeyDown={async e => {
                  if (e.key === "Enter" && newPostTypeName.trim()) {
                    try {
                      const res = await ChatService.savePostType({ roomId: activeRoom._id, name: newPostTypeName.trim(), color: newPostTypeColor, icon: newPostTypeIcon });
                      if (res.Good) { setCustomPostTypes(res.customPostTypes); setNewPostTypeName(""); setShowCreatePostTypeModal(false); }
                    } catch (err) { message.error("Failed to create post type"); }
                  }
                }}
                autoFocus
              />
              <label>Post Type Name *</label>
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label style={{ fontSize: "12px", fontWeight: "600", color: "#1d1d1d", display: "block", marginBottom: "10px" }}>Icon</label>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                {[
                  { name: "Tag", icon: <Tag size={16} /> },
                  { name: "Star", icon: <Star size={16} /> },
                  { name: "Zap", icon: <Zap size={16} /> },
                  { name: "Heart", icon: <Heart size={16} /> },
                  { name: "Flag", icon: <Flag size={16} /> },
                  { name: "Bookmark", icon: <Bookmark size={16} /> },
                  { name: "AlertCircle", icon: <AlertCircle size={16} /> },
                  { name: "CheckCircle", icon: <CheckCircle size={16} /> },
                  { name: "Megaphone", icon: <Megaphone size={16} /> },
                  { name: "Bell", icon: <Bell size={16} /> },
                  { name: "Lightbulb", icon: <Lightbulb size={16} /> },
                  { name: "MessagesSquare", icon: <MessagesSquare size={16} /> },
                ].map(({ name, icon }) => (
                  <div
                    key={name}
                    onClick={() => setNewPostTypeIcon(name)}
                    style={{ width: 36, height: 36, borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", border: newPostTypeIcon === name ? `2px solid ${newPostTypeColor}` : "2px solid #e0e0e0", background: newPostTypeIcon === name ? newPostTypeColor + "22" : "#f8f9fa", color: newPostTypeIcon === name ? newPostTypeColor : "#5f6368", transition: "all 0.15s" }}
                  >
                    {icon}
                  </div>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label style={{ fontSize: "12px", fontWeight: "600", color: "#1d1d1d", display: "block", marginBottom: "10px" }}>Color</label>
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
                {["#0DDFAB", "#1a73e8", "#ea4335", "#fbbc04", "#34a853", "#ff6d00", "#9c27b0", "#e91e63", "#00bcd4", "#ff5722"].map(c => (
                  <div
                    key={c}
                    onClick={() => setNewPostTypeColor(c)}
                    style={{ width: 28, height: 28, borderRadius: "50%", background: c, cursor: "pointer", border: newPostTypeColor === c ? "3px solid #1d1d1d" : "3px solid transparent", transition: "border 0.15s", boxSizing: "border-box" }}
                  />
                ))}
                <input
                  type="color"
                  value={newPostTypeColor}
                  onChange={e => setNewPostTypeColor(e.target.value)}
                  style={{ width: 28, height: 28, borderRadius: "50%", border: "none", padding: 0, cursor: "pointer", background: "none" }}
                  title="Custom color"
                />
              </div>
            </div>

            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button
                style={{ padding: "8px 20px", border: "1.5px solid #d0d0d0", borderRadius: "8px", background: "#fff", fontSize: "13px", fontWeight: "500", color: "#1d1d1d", cursor: "pointer" }}
                onClick={() => setShowCreatePostTypeModal(false)}
              >
                Cancel
              </button>
              <button
                style={{ padding: "8px 20px", border: "none", borderRadius: "8px", background: newPostTypeName.trim() ? "#0DDFAB" : "#d0d0d0", fontSize: "13px", fontWeight: "600", color: "#fff", cursor: newPostTypeName.trim() ? "pointer" : "not-allowed" }}
                onClick={async () => {
                  if (!newPostTypeName.trim()) return;
                  try {
                    const res = await ChatService.savePostType({ roomId: activeRoom._id, name: newPostTypeName.trim(), color: newPostTypeColor, icon: newPostTypeIcon });
                    if (res.Good) { setCustomPostTypes(res.customPostTypes); setNewPostTypeName(""); setShowCreatePostTypeModal(false); }
                  } catch (err) { message.error("Failed to create post type"); }
                }}
              >
                + Create Post Type
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Permission dropdown — Chat Member list edit (independent of popover state) */}
      {chatMemberEditId && (() => {
        const permOptions = [
          { label: "Full edit", desc: "Can add people, create post types, send messages, and manage the channel." },
          { label: "Edit", desc: "Can create post types and send messages. Can't add people to the channel." },
          { label: "Comment", desc: "Can send messages using existing post types. Can't create new post types or add people." },
          { label: "View Only", desc: "Read only. Can view messages but cannot send or interact." },
        ];
        const perm = memberPermissions[String(chatMemberEditId)] || "Full edit";
        return (
          <>
            <div style={{ position: "fixed", inset: 0, zIndex: 100019 }} onClick={() => setChatMemberEditId(null)} />
            <div
              style={{ position: "fixed", top: chatMemberEditPos.top, left: chatMemberEditPos.left, background: "#fff", border: "1px solid #e0e0e0", borderRadius: "12px", boxShadow: "0 6px 24px rgba(0,0,0,0.15)", zIndex: 100020, width: 250, padding: "6px" }}
              onClick={e => e.stopPropagation()}
            >
              {permOptions.map(opt => (
                <div
                  key={opt.label}
                  style={{ padding: "10px 12px", borderRadius: "8px", cursor: "pointer", border: perm === opt.label ? "1.5px solid #e0e0e0" : "1.5px solid transparent", marginBottom: "2px" }}
                  onMouseOver={e => { if (perm !== opt.label) e.currentTarget.style.background = "#f8f9fa"; }}
                  onMouseOut={e => { if (perm !== opt.label) e.currentTarget.style.background = "transparent"; }}
                  onClick={() => {
                    setMemberPermissions(prev => ({ ...prev, [String(chatMemberEditId)]: opt.label }));
                    ChatService.updatePermission({ roomId: activeRoom._id, userId: chatMemberEditId, permission: opt.label }).catch(() => {});
                    setChatMemberEditId(null);
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "3px" }}>
                    <span style={{ fontSize: "13px", fontWeight: "600", color: "#1d1d1d" }}>{opt.label}</span>
                    {perm === opt.label && <span style={{ color: "#1d1d1d", fontSize: 14, fontWeight: 700 }}>✓</span>}
                  </div>
                  <div style={{ fontSize: "11px", color: "#9e9e9e", lineHeight: 1.4 }}>{opt.desc}</div>
                </div>
              ))}
            </div>
          </>
        );
      })()}

      {/* Permission dropdown — fixed position to escape modal overflow */}
      {(showShareModal || chatMemberPopoverOpen) && openPermDropdown && (() => {
        const permOptions = [
          { label: "Full edit", desc: "Can add people, create post types, send messages, and manage the channel." },
          { label: "Edit", desc: "Can create post types and send messages. Can't add people to the channel." },
          { label: "Comment", desc: "Can send messages using existing post types. Can't create new post types or add people." },
          { label: "View Only", desc: "Read only. Can view messages but cannot send or interact." },
        ];
        const isInvite = openPermDropdown === "__invite__";
        const perm = isInvite ? inviteAccess : (memberPermissions[String(openPermDropdown)] || "Full edit");
        return (
          <div
            style={{ position: "fixed", top: permDropdownPos.top, right: permDropdownPos.right, background: "#fff", border: "1px solid #e0e0e0", borderRadius: "12px", boxShadow: "0 6px 24px rgba(0,0,0,0.15)", zIndex: 100020, width: 250, padding: "6px" }}
            onClick={e => e.stopPropagation()}
          >
            {permOptions.map(opt => (
              <div
                key={opt.label}
                style={{ padding: "10px 12px", borderRadius: "8px", cursor: "pointer", border: perm === opt.label ? "1.5px solid #e0e0e0" : "1.5px solid transparent", marginBottom: "2px" }}
                onMouseOver={e => { if (perm !== opt.label) e.currentTarget.style.background = "#f8f9fa"; }}
                onMouseOut={e => { if (perm !== opt.label) e.currentTarget.style.background = "transparent"; }}
                onClick={() => {
                  if (isInvite) {
                    setInviteAccess(opt.label);
                    setInviteLink(""); // clear stale link — must regenerate with new permission
                  } else {
                    setMemberPermissions(prev => ({ ...prev, [String(openPermDropdown)]: opt.label }));
                    ChatService.updatePermission({ roomId: activeRoom._id, userId: openPermDropdown, permission: opt.label }).catch(() => {});
                  }
                  setOpenPermDropdown(null);
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "3px" }}>
                  <span style={{ fontSize: "13px", fontWeight: "600", color: "#1d1d1d" }}>{opt.label}</span>
                  {perm === opt.label && <span style={{ color: "#1d1d1d", fontSize: 14, fontWeight: 700 }}>✓</span>}
                </div>
                <div style={{ fontSize: "11px", color: "#9e9e9e", lineHeight: 1.4 }}>{opt.desc}</div>
              </div>
            ))}
          </div>
        );
      })()}
      {/* Hidden trigger for delete message modal */}

      {/* Forward Message Modal */}
      {showForwardModal && (
        <div
          style={{ position: "fixed", inset: 0, zIndex: 13000, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.35)" }}
          onClick={closeForwardModal}
        >
          <div
            style={{ background: "#fff", borderRadius: "14px", width: 380, maxWidth: "95vw", maxHeight: "70vh", display: "flex", flexDirection: "column", boxShadow: "0 8px 40px rgba(0,0,0,0.18)", overflow: "hidden" }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px 10px" }}>
              <span style={{ fontSize: "14px", fontWeight: "600", color: "#1d1d1d" }}>Forward Message</span>
              <X size={16} color="#5f6368" style={{ cursor: "pointer" }} onClick={closeForwardModal} />
            </div>

            <div style={{ padding: "0 18px 14px", display: "flex", flexDirection: "column", gap: "10px", flex: 1, minHeight: 0 }}>
              {/* Message preview */}
              {forwardMsg && (
                <div style={{ position: "relative", border: "1.5px solid #d0d0d0", borderRadius: "8px", padding: "7px 12px" }}>
                  <span style={{ position: "absolute", top: "-8px", left: "10px", background: "#fff", padding: "0 4px", fontSize: "10px", color: "#5f6368", fontWeight: "500" }}>Message</span>
                  <div style={{ fontSize: "11px", color: "#0DDFAB", fontWeight: "600", marginBottom: "2px" }}>{forwardMsg.sender_name || "Unknown"}</div>
                  <div style={{ fontSize: "12px", color: "#1d1d1d", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {forwardMsg.text || (forwardMsg.attachments?.length ? "📎 Attachment" : "...")}
                  </div>
                </div>
              )}

              {/* Search */}
              <div className="float-input-wrap float-input-icon">
                <i className="uil-search" style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", color: "#aaa", fontSize: "13px", pointerEvents: "none", zIndex: 2 }} />
                <input
                  type="text"
                  autoComplete="new-password"
                  data-form-type="other"
                  placeholder=" "
                  className="form-control-custom-focus"
                  value={forwardSearch}
                  onChange={e => setForwardSearch(e.target.value)}
                  style={{ padding: "0 32px 0 12px", borderRadius: "8px", height: "36px", border: "1.5px solid #d0d0d0", lineHeight: "36px", width: "100%", fontSize: "12px" }}
                  autoFocus
                />
                <label style={{ fontSize: "12px", left: "12px" }}>Search conversations...</label>
              </div>

              {/* Who to forward to */}
              <div style={{ fontSize: "11px", fontWeight: "600", color: "#5f6368", textTransform: "uppercase", letterSpacing: "0.5px" }}>Forward to</div>
              <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", marginTop: "-4px" }}>
                {(() => {
                  const filtered = rooms.filter(r => {
                    const name = (r.name || r.members?.map(m => `${m.first_name || ""} ${m.last_name || ""}`.trim()).join(", ") || "").toLowerCase();
                    return name.includes(forwardSearch.toLowerCase());
                  });
                  if (filtered.length === 0) return (
                    <div style={{ textAlign: "center", color: "#9e9e9e", fontSize: "12px", marginTop: "16px" }}>No conversations found</div>
                  );
                  return (
                    <>
                      {filtered.map(r => {
                        const displayName = r.name || r.members?.filter(m => String(m.id || m._id) !== String(currentUserId)).map(m => `${m.first_name || ""} ${m.last_name || ""}`.trim()).join(", ") || "Conversation";
                        const initials = displayName.slice(0, 2).toUpperCase();
                        const isSending = forwardSendingIds.has(r._id);
                        const isSent = forwardSentIds.has(r._id);
                        const isChannel = r.type === "channel" || r.type === "project";
                        return (
                          <div key={r._id} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "5px 0", borderBottom: "1px solid #f5f5f5" }}>
                            <div style={{ width: 26, height: 26, borderRadius: "6px", background: "#0DDFAB", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                              {isChannel ? <Hash size={12} /> : initials}
                            </div>
                            <div style={{ flex: 1, overflow: "hidden" }}>
                              <div style={{ fontSize: "12px", fontWeight: "500", color: "#1d1d1d", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{displayName}</div>
                              <div style={{ fontSize: "10px", color: "#9e9e9e" }}>{r.type === "channel" ? "Channel" : r.type === "project" ? "Project" : "Direct Message"}</div>
                            </div>
                            <button
                              onClick={() => !isSent && handleForwardMessage(r)}
                              disabled={isSending || isSent}
                              style={{ height: "26px", padding: "0 12px", border: "none", borderRadius: "6px", background: isSent ? "rgba(13,223,171,0.45)" : isSending ? "#d0d0d0" : "#0DDFAB", color: "#fff", fontSize: "11px", fontWeight: "600", cursor: (isSending || isSent) ? "default" : "pointer", flexShrink: 0, minWidth: "54px", pointerEvents: isSent ? "none" : "auto" }}
                            >
                              {isSent ? "Sent" : isSending ? "..." : "Send"}
                            </button>
                          </div>
                        );
                      })}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      <div id="openChatMsgDeleteModal" data-bs-toggle="modal" data-bs-target="#deleteChatMessageModal" style={{ display: "none" }} />

      {/* Delete Message Confirmation Modal */}
      <div id="deleteChatMessageModal" className="modal fade" tabIndex={-1} role="dialog" aria-hidden="true" data-bs-keyboard="false">
        <div className="modal-dialog modal-dialog-centered" style={{ display: "flex", alignItems: "center", minHeight: "calc(100vh - 3.5rem)", margin: "auto" }}>
          <div className="modal-content" style={{ borderRadius: "12px", border: "none", padding: "8px 4px" }}>
            <div className="modal-header border-0 pb-1">
              <h5 className="modal-title fw-semibold" style={{ fontSize: "16px" }}>Delete Message</h5>
              <button type="button" className="btn-close" id="closeChatMsgDeleteModal" data-bs-dismiss="modal" aria-label="Close" onClick={() => { setMessageToDelete(null); setDeleteFor("everyone"); }} />
            </div>
            <div className="modal-body pt-2 pb-3">
              <p style={{ fontSize: "14px", color: "#1D1D1D", marginBottom: "14px" }}>Who do you want to delete this message for?</p>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                <label style={{ display: "flex", alignItems: "flex-start", gap: "10px", cursor: "pointer", padding: "10px 12px", borderRadius: "8px", border: deleteFor === "self" ? "1.5px solid #0DDFAB" : "1.5px solid #e0e0e0", background: deleteFor === "self" ? "rgba(13,223,171,0.05)" : "#fff", transition: "all 0.15s" }}>
                  <input type="radio" name="deleteFor" value="self" checked={deleteFor === "self"} onChange={() => setDeleteFor("self")} style={{ marginTop: "2px", accentColor: "#0DDFAB" }} />
                  <div>
                    <div style={{ fontSize: "13px", fontWeight: "600", color: "#1D1D1D" }}>Delete for me</div>
                    <div style={{ fontSize: "12px", color: "#6c757d", marginTop: "2px" }}>This message will be removed only from your view.</div>
                  </div>
                </label>
                <label style={{ display: "flex", alignItems: "flex-start", gap: "10px", cursor: "pointer", padding: "10px 12px", borderRadius: "8px", border: deleteFor === "everyone" ? "1.5px solid #ff4d4f" : "1.5px solid #e0e0e0", background: deleteFor === "everyone" ? "rgba(255,77,79,0.04)" : "#fff", transition: "all 0.15s" }}>
                  <input type="radio" name="deleteFor" value="everyone" checked={deleteFor === "everyone"} onChange={() => setDeleteFor("everyone")} style={{ marginTop: "2px", accentColor: "#ff4d4f" }} />
                  <div>
                    <div style={{ fontSize: "13px", fontWeight: "600", color: "#1D1D1D" }}>Delete for everyone</div>
                    <div style={{ fontSize: "12px", color: "#6c757d", marginTop: "2px" }}>This message will be permanently deleted for all members.</div>
                  </div>
                </label>
              </div>
            </div>
            <div className="modal-footer border-0 pt-1">
              <button type="button" className={`btn waves-effect waves-light ${deleteFor === "everyone" ? "btn-danger" : "btn-primary"}`} onClick={confirmDeleteMessage} style={{ borderRadius: "5px", fontSize: "13px", background: deleteFor === "self" ? "#0DDFAB" : undefined, borderColor: deleteFor === "self" ? "#0DDFAB" : undefined }}>
                {deleteFor === "everyone" ? "Delete for Everyone" : "Delete for Me"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChatModal;
